import LinkHeader from 'http-link-header';
import { quad, literal, namedNode, blankNode, dataset } from '@rdfjs/dataset';
import { Writer, Parser } from 'n3';
import { Response } from 'cross-fetch';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/** @internal */
function internal_toIriString(iri) {
    return typeof iri === "string" ? iri : iri.value;
}
/**
 * Verify whether a given SolidDataset includes metadata about where it was sent to.
 *
 * @param dataset A [[SolidDataset]] that may have metadata attached about the Resource it was retrieved from.
 * @returns True if `dataset` includes metadata about the Resource it was sent to, false if not.
 * @since 0.2.0
 */
function hasResourceInfo(resource) {
    const potentialResourceInfo = resource;
    return (typeof potentialResourceInfo === "object" &&
        typeof potentialResourceInfo.internal_resourceInfo === "object");
}
/**
 * Verify whether a given SolidDataset includes metadata about where it was retrieved from.
 *
 * @param dataset A [[SolidDataset]] that may have metadata attached about the Resource it was retrieved from.
 * @returns True if `dataset` includes metadata about the Resource it was retrieved from, false if not.
 * @since 0.6.0
 */
function hasServerResourceInfo(resource) {
    const potentialResourceInfo = resource;
    return (typeof potentialResourceInfo === "object" &&
        typeof potentialResourceInfo.internal_resourceInfo === "object" &&
        typeof potentialResourceInfo.internal_resourceInfo.linkedResources ===
            "object");
}
/** @internal */
function hasChangelog(dataset) {
    const potentialChangeLog = dataset;
    return (typeof potentialChangeLog.internal_changeLog === "object" &&
        Array.isArray(potentialChangeLog.internal_changeLog.additions) &&
        Array.isArray(potentialChangeLog.internal_changeLog.deletions));
}
/**
 * Given a [[SolidDataset]], verify whether its Access Control List is accessible to the current user.
 *
 * This should generally only be true for SolidDatasets fetched by
 * [[getSolidDatasetWithAcl]].
 *
 * Please note that the Web Access Control specification is not yet finalised, and hence, this
 * function is still experimental and can change in a non-major release.
 *
 * @param dataset A [[SolidDataset]].
 * @returns Whether the given `dataset` has a an ACL that is accessible to the current user.
 */
function hasAccessibleAcl(dataset) {
    return typeof dataset.internal_resourceInfo.aclUrl === "string";
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * @ignore Internal fallback for when no fetcher is provided; not to be used downstream.
 */
const fetch = (resource, init) => {
    /* istanbul ignore if: `require` is always defined in the unit test environment */
    if (typeof window === "object" && typeof require !== "function") {
        return window.fetch;
    }
    // Implementation note: it's up to the client application to resolve these module names to the
    // respective npm packages. At least one commonly used tool (Webpack) is only able to do that if
    // the module names are literal strings.
    // Additionally, Webpack throws a warning in a way that halts compilation for at least Next.js
    // when using native Javascript dynamic imports (`import()`), whereas `require()` just logs a
    // warning. Since the use of package names instead of file names requires a bundles anyway, this
    // should not have any practical consequences. For more background, see:
    // https://github.com/webpack/webpack/issues/7713
    let fetch;
    // Unfortunately solid-client-authn-browser does not support a default session yet.
    // Once it does, we can auto-detect if it is available and use it as follows:
    // try {
    //   fetch = require("solid-client-authn-browser").fetch;
    // } catch (e) {
    // eslint-disable-next-line prefer-const
    fetch = require("cross-fetch");
    // }
    return fetch(resource, init);
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
// TODO: These should be replaced by auto-generated constants,
//       if we can ensure that unused constants will be excluded from bundles.
/** @hidden */
const acl = {
    Authorization: "http://www.w3.org/ns/auth/acl#Authorization",
    accessTo: "http://www.w3.org/ns/auth/acl#accessTo",
    agent: "http://www.w3.org/ns/auth/acl#agent",
    agentGroup: "http://www.w3.org/ns/auth/acl#agentGroup",
    agentClass: "http://www.w3.org/ns/auth/acl#agentClass",
    default: "http://www.w3.org/ns/auth/acl#default",
    defaultForNew: "http://www.w3.org/ns/auth/acl#defaultForNew",
    mode: "http://www.w3.org/ns/auth/acl#mode",
    origin: "http://www.w3.org/ns/auth/acl#origin",
};
/** @hidden */
const rdf = {
    type: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
};
/** @hidden */
const foaf = {
    Agent: "http://xmlns.com/foaf/0.1/Agent",
};
/** @hidden */
const acp = {
    Policy: "http://www.w3.org/ns/solid/acp#Policy",
    AccessControl: "http://www.w3.org/ns/solid/acp#AccessControl",
    Read: "http://www.w3.org/ns/solid/acp#Read",
    Append: "http://www.w3.org/ns/solid/acp#Append",
    Write: "http://www.w3.org/ns/solid/acp#Write",
    Rule: "http://www.w3.org/ns/solid/acp#Rule",
    accessControl: "http://www.w3.org/ns/solid/acp#accessControl",
    apply: "http://www.w3.org/ns/solid/acp#apply",
    applyMembers: "http://www.w3.org/ns/solid/acp#applyMembers",
    allow: "http://www.w3.org/ns/solid/acp#allow",
    deny: "http://www.w3.org/ns/solid/acp#deny",
    allOf: "http://www.w3.org/ns/solid/acp#allOf",
    anyOf: "http://www.w3.org/ns/solid/acp#anyOf",
    noneOf: "http://www.w3.org/ns/solid/acp#noneOf",
    access: "http://www.w3.org/ns/solid/acp#access",
    accessMembers: "http://www.w3.org/ns/solid/acp#accessMembers",
    agent: "http://www.w3.org/ns/solid/acp#agent",
    group: "http://www.w3.org/ns/solid/acp#group",
    PublicAgent: "http://www.w3.org/ns/solid/acp#PublicAgent",
    AuthenticatedAgent: "http://www.w3.org/ns/solid/acp#AuthenticatedAgent",
    CreatorAgent: "http://www.w3.org/ns/solid/acp#CreatorAgent",
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * @internal
 */
const DataFactory = { quad, literal, namedNode, blankNode };
/**
 * Clone a Dataset.
 *
 * Note that the Quads are not cloned, i.e. if you modify the Quads in the output Dataset, the Quads
 * in the input Dataset will also be changed.
 *
 * @internal
 * @param input Dataset to clone.
 * @returns A new Dataset with the same Quads as `input`.
 */
function clone(input) {
    const output = dataset();
    for (const quad of input) {
        output.add(quad);
    }
    return output;
}
/**
 * @internal
 * @param input Dataset to clone.
 * @param callback Function that takes a Quad, and returns a boolean indicating whether that Quad should be included in the cloned Dataset.
 * @returns A new Dataset with the same Quads as `input`, excluding the ones for which `callback` returned `false`.
 */
function filter(input, callback) {
    const output = dataset();
    for (const quad of input) {
        if (callback(quad)) {
            output.add(quad);
        }
    }
    return output;
}
/**
 * Verify whether a given value has the required DatasetCore properties.
 *
 * @param input Value that might or might not be a DatasetCore
 * @returns Whether `input` provides the properties prescribed by the RDF/JS Dataset spec 1.0.
 * @hidden This is an internal convenience function.
 */
function internal_isDatasetCore(input) {
    return (typeof input === "object" &&
        typeof input.size === "number" &&
        typeof input.add === "function" &&
        typeof input.delete === "function" &&
        typeof input.has === "function" &&
        typeof input.match === "function" &&
        Array.from(input).length === input.size);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * @param quads Triples that should be serialised to Turtle
 * @internal Utility method for internal use; not part of the public API.
 */
function triplesToTurtle(quads) {
    return __awaiter(this, void 0, void 0, function* () {
        const format = "text/turtle";
        const writer = new Writer({ format: format });
        // Remove any potentially lingering references to Named Graphs in Quads;
        // they'll be determined by the URL the Turtle will be sent to:
        const triples = quads.map((quad) => DataFactory.quad(quad.subject, quad.predicate, quad.object, undefined));
        writer.addQuads(triples);
        const writePromise = new Promise((resolve, reject) => {
            writer.end((error, result) => {
                /* istanbul ignore if [n3.js doesn't actually pass an error nor a result, apparently: https://github.com/rdfjs/N3.js/blob/62682e48c02d8965b4d728cb5f2cbec6b5d1b1b8/src/N3Writer.js#L290] */
                if (error) {
                    return reject(error);
                }
                resolve(result);
            });
        });
        const rawTurtle = yield writePromise;
        return rawTurtle;
    });
}
/**
 * @param raw Turtle that should be parsed into Triples
 * @internal Utility method for internal use; not part of the public API.
 */
function turtleToTriples(raw, resourceIri) {
    return __awaiter(this, void 0, void 0, function* () {
        const format = "text/turtle";
        const parser = new Parser({ format: format, baseIRI: resourceIri });
        const parsingPromise = new Promise((resolve, reject) => {
            const parsedTriples = [];
            parser.parse(raw, (error, triple, _prefixes) => {
                if (error) {
                    return reject(error);
                }
                if (triple) {
                    parsedTriples.push(triple);
                }
                else {
                    resolve(parsedTriples);
                }
            });
        });
        return parsingPromise;
    });
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * IRIs of the XML Schema data types we support
 * @internal
 */
const xmlSchemaTypes = {
    boolean: "http://www.w3.org/2001/XMLSchema#boolean",
    dateTime: "http://www.w3.org/2001/XMLSchema#dateTime",
    decimal: "http://www.w3.org/2001/XMLSchema#decimal",
    integer: "http://www.w3.org/2001/XMLSchema#integer",
    string: "http://www.w3.org/2001/XMLSchema#string",
    langString: "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString",
};
/**
 * @internal
 * @param value Value to serialise.
 * @returns String representation of `value`.
 * @see https://www.w3.org/TR/xmlschema-2/#boolean-lexical-representation
 */
function serializeBoolean(value) {
    return value ? "true" : "false";
}
/**
 * @internal
 * @param value Value to deserialise.
 * @returns Deserialized boolean, or null if the given value is not a valid serialised boolean.
 * @see https://www.w3.org/TR/xmlschema-2/#boolean-lexical-representation
 */
function deserializeBoolean(value) {
    if (value === "true" || value === "1") {
        return true;
    }
    else if (value === "false" || value === "0") {
        return false;
    }
    else {
        return null;
    }
}
/**
 * @internal
 * @param value Value to serialise.
 * @returns String representation of `value`.
 * @see https://www.w3.org/TR/xmlschema-2/#dateTime-lexical-representation
 */
function serializeDatetime(value) {
    // Although the XML Schema DateTime is not _exactly_ an ISO 8601 string
    // (see https://www.w3.org/TR/xmlschema-2/#deviantformats),
    // the deviations only affect the parsing, not the serialisation.
    // Therefore, we can just use .toISOString():
    return value.toISOString();
}
/**
 * @internal
 * @param value Value to deserialise.
 * @returns Deserialized datetime, or null if the given value is not a valid serialised datetime.
 * @see https://www.w3.org/TR/xmlschema-2/#dateTime-lexical-representation
 */
function deserializeDatetime(literalString) {
    // DateTime in the format described at
    // https://www.w3.org/TR/xmlschema-2/#dateTime-lexical-representation
    // (without constraints on the value).
    // -? - An optional leading `-`.
    // \d{4,}- - Four or more digits followed by a `-` representing the year. Example: "3000-".
    // \d\d-\d\d - Two digits representing the month and two representing the day of the month,
    //             separated by a `-`. Example: "11-03".
    // T - The letter T, separating the date from the time.
    // \d\d:\d\d:\d\d - Two digits for the hour, minute and second, respectively, separated by a `:`.
    //                  Example: "13:37:42".
    // (\.\d+)? - Optionally a `.` followed by one or more digits representing milliseconds.
    //            Example: ".1337".
    // (Z|(\+|-)\d\d:\d\d) - The letter Z indicating UTC, or a `+` or `-` followed by two digits for
    //                       the hour offset and two for the minute offset, separated by a `:`.
    //                       Example: "+13:37".
    const datetimeRegEx = /-?\d{4,}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(Z|(\+|-)\d\d:\d\d)/;
    if (!datetimeRegEx.test(literalString)) {
        return null;
    }
    const [signedDateString, rest] = literalString.split("T");
    // The date string can optionally be prefixed with `-`,
    // in which case the year is negative:
    const [yearMultiplier, dateString] = signedDateString.charAt(0) === "-"
        ? [-1, signedDateString.substring(1)]
        : [1, signedDateString];
    const [yearString, monthString, dayString] = dateString.split("-");
    const utcFullYear = Number.parseInt(yearString, 10) * yearMultiplier;
    const utcMonth = Number.parseInt(monthString, 10) - 1;
    const utcDate = Number.parseInt(dayString, 10);
    const [timeString, timezoneString] = splitTimeFromTimezone(rest);
    const [hourOffset, minuteOffset] = getTimezoneOffsets(timezoneString);
    const [hourString, minuteString, timeRest] = timeString.split(":");
    const utcHours = Number.parseInt(hourString, 10) + hourOffset;
    const utcMinutes = Number.parseInt(minuteString, 10) + minuteOffset;
    const [secondString, optionalMillisecondString] = timeRest.split(".");
    const utcSeconds = Number.parseInt(secondString, 10);
    const utcMilliseconds = optionalMillisecondString
        ? Number.parseInt(optionalMillisecondString, 10)
        : 0;
    const date = new Date(Date.UTC(utcFullYear, utcMonth, utcDate, utcHours, utcMinutes, utcSeconds, utcMilliseconds));
    return date;
}
/**
 * @param timeString An XML Schema time string.
 * @returns A tuple [timeString, timezoneString].
 * @see https://www.w3.org/TR/xmlschema-2/#time-lexical-repr
 */
function splitTimeFromTimezone(timeString) {
    if (timeString.endsWith("Z")) {
        return [timeString.substring(0, timeString.length - 1), "Z"];
    }
    const splitOnPlus = timeString.split("+");
    const splitOnMinus = timeString.split("-");
    return splitOnPlus.length > splitOnMinus.length
        ? [splitOnPlus[0], "+" + splitOnPlus[1]]
        : [splitOnMinus[0], "-" + splitOnMinus[1]];
}
/**
 * @param timezoneString Lexical representation of a time zone in XML Schema.
 * @returns A tuple of the hour and minute offset of the time zone.
 * @see https://www.w3.org/TR/xmlschema-2/#dateTime-timezones
 */
function getTimezoneOffsets(timezoneString) {
    if (timezoneString === "Z") {
        return [0, 0];
    }
    const multiplier = timezoneString.charAt(0) === "+" ? 1 : -1;
    const [hourString, minuteString] = timezoneString.substring(1).split(":");
    const hours = Number.parseInt(hourString, 10);
    const minutes = Number.parseInt(minuteString, 10);
    return [hours * multiplier, minutes * multiplier];
}
/**
 * @internal
 * @param value Value to serialise.
 * @returns String representation of `value`.
 * @see https://www.w3.org/TR/xmlschema-2/#decimal-lexical-representation
 */
function serializeDecimal(value) {
    return value.toString();
}
/**
 * @internal
 * @param value Value to deserialise.
 * @returns Deserialized decimal, or null if the given value is not a valid serialised decimal.
 * @see https://www.w3.org/TR/xmlschema-2/#decimal-lexical-representation
 */
function deserializeDecimal(literalString) {
    const deserialized = Number.parseFloat(literalString);
    if (Number.isNaN(deserialized)) {
        return null;
    }
    return deserialized;
}
/**
 * @internal
 * @param value Value to serialise.
 * @returns String representation of `value`.
 */
function serializeInteger(value) {
    return value.toString();
}
/**
 * @internal
 * @param value Value to deserialise.
 * @returns Deserialized integer, or null if the given value is not a valid serialised integer.
 */
function deserializeInteger(literalString) {
    const deserialized = Number.parseInt(literalString, 10);
    if (Number.isNaN(deserialized)) {
        return null;
    }
    return deserialized;
}
/**
 * @internal
 * @param locale Locale to transform into a consistent format.
 */
function normalizeLocale(locale) {
    return locale.toLowerCase();
}
/**
 * @internal Library users shouldn't need to be exposed to raw NamedNodes.
 * @param value The value that might or might not be a Named Node.
 * @returns Whether `value` is a Named Node.
 */
function isNamedNode(value) {
    return isTerm(value) && value.termType === "NamedNode";
}
/**
 * @internal Library users shouldn't need to be exposed to raw Literals.
 * @param value The value that might or might not be a Literal.
 * @returns Whether `value` is a Literal.
 */
function isLiteral(value) {
    return isTerm(value) && value.termType === "Literal";
}
/**
 * @internal Library users shouldn't need to be exposed to raw Terms.
 * @param value The value that might or might not be a Term.
 * @returns Whether `value` is a Term.
 */
function isTerm(value) {
    return (value !== null &&
        typeof value === "object" &&
        typeof value.termType === "string" &&
        typeof value.value === "string" &&
        typeof value.equals === "function");
}
/**
 * @internal Library users shouldn't need to be exposed to LocalNodes.
 * @param value The value that might or might not be a Node with no known IRI yet.
 * @returns Whether `value` is a Node with no known IRI yet.
 */
function isLocalNode(value) {
    return (isTerm(value) &&
        value.termType === "BlankNode" &&
        typeof value.internal_name === "string");
}
/**
 * Construct a new LocalNode.
 *
 * @internal Library users shouldn't need to be exposed to LocalNodes.
 * @param name Name to identify this node by.
 * @returns A LocalNode whose name will be resolved when it is persisted to a Pod.
 */
function getLocalNode(name) {
    const localNode = Object.assign(DataFactory.blankNode(), {
        internal_name: name,
    });
    return localNode;
}
/**
 * Ensure that a given value is a Named Node.
 *
 * If the given parameter is a Named Node already, it will be returned as-is. If it is a string, it
 * will check whether it is a valid IRI. If not, it will throw an error; otherwise a Named Node
 * representing the given IRI will be returned.
 *
 * @internal Library users shouldn't need to be exposed to raw NamedNodes.
 * @param iri The IRI that should be converted into a Named Node, if it isn't one yet.
 */
function asNamedNode(iri) {
    if (isNamedNode(iri)) {
        return iri;
    }
    // If the runtime environment supports URL, instantiate one.
    // If the given IRI is not a valid URL, it will throw an error.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/URL
    /* istanbul ignore else [URL is available in our testing environment, so we cannot test the alternative] */
    if (typeof URL !== "undefined") {
        new URL(iri);
    }
    return DataFactory.namedNode(iri);
}
/**
 * Check whether two current- or potential NamedNodes are/will be equal.
 *
 * @internal Utility method; library users should not need to interact with LocalNodes directly.
 */
function isEqual(node1, node2, options = {}) {
    if (isNamedNode(node1) && isNamedNode(node2)) {
        return node1.equals(node2);
    }
    if (isLocalNode(node1) && isLocalNode(node2)) {
        return node1.internal_name === node2.internal_name;
    }
    if (typeof options.resourceIri === "undefined") {
        // If we don't know what IRI to resolve the LocalNode to,
        // we cannot conclude that it is equal to the NamedNode's full IRI:
        return false;
    }
    const namedNode1 = isNamedNode(node1)
        ? node1
        : resolveIriForLocalNode(node1, options.resourceIri);
    const namedNode2 = isNamedNode(node2)
        ? node2
        : resolveIriForLocalNode(node2, options.resourceIri);
    return namedNode1.equals(namedNode2);
}
/**
 * @internal Utility method; library users should not need to interact with LocalNodes directly.
 * @param quad The Quad to resolve LocalNodes in.
 * @param resourceIri The IRI of the Resource to resolve the LocalNodes against.
 */
function resolveIriForLocalNodes(quad, resourceIri) {
    const subject = isLocalNode(quad.subject)
        ? resolveIriForLocalNode(quad.subject, resourceIri)
        : quad.subject;
    const object = isLocalNode(quad.object)
        ? resolveIriForLocalNode(quad.object, resourceIri)
        : quad.object;
    return Object.assign(Object.assign({}, quad), { subject: subject, object: object });
}
/**
 * @internal Utility method; library users should not need to interact with LocalNodes directly.
 * @param localNode The LocalNode to resolve to a NamedNode.
 * @param resourceIri The Resource in which the Node will be saved.
 */
function resolveIriForLocalNode(localNode, resourceIri) {
    return DataFactory.namedNode(resolveLocalIri(localNode.internal_name, resourceIri));
}
/**
 * @internal API for internal use only.
 * @param name The name identifying a Thing.
 * @param resourceIri The Resource in which the Thing can be found.
 */
function resolveLocalIri(name, resourceIri) {
    /* istanbul ignore if [The URL interface is available in the testing environment, so we cannot test this] */
    if (typeof URL === "undefined") {
        throw new Error("The URL interface is not available, so an IRI cannot be determined.");
    }
    const thingIri = new URL(resourceIri);
    thingIri.hash = name;
    return thingIri.href;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * @param thing The [[Thing]] to read a URL value from.
 * @param property The given Property for which you want the URL value.
 * @returns A URL value for the given Property, if present, or null otherwise.
 */
function getUrl(thing, property) {
    const namedNodeMatcher = getNamedNodeMatcher(property);
    const matchingQuad = findOne(thing, namedNodeMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object.value;
}
/** @hidden Alias of [[getUrl]] for those who prefer IRI terminology. */
const getIri = getUrl;
/**
 * @param thing The [[Thing]] to read the URL values from.
 * @param property The given Property for which you want the URL values.
 * @returns The URL values for the given Property.
 */
function getUrlAll(thing, property) {
    const iriMatcher = getNamedNodeMatcher(property);
    const matchingQuads = findAll(thing, iriMatcher);
    return matchingQuads.map((quad) => quad.object.value);
}
/** @hidden Alias of [[getUrlAll]] for those who prefer IRI terminology. */
const getIriAll = getUrlAll;
/**
 * @param thing The [[Thing]] to read a boolean value from.
 * @param property The given Property for which you want the boolean value.
 * @returns A boolean value for the given Property, if present, or null otherwise.
 */
function getBoolean(thing, property) {
    const literalString = getLiteralOfType(thing, property, xmlSchemaTypes.boolean);
    if (literalString === null) {
        return null;
    }
    return deserializeBoolean(literalString);
}
/**
 * @param thing The [[Thing]] to read the boolean values from.
 * @param property The given Property for which you want the boolean values.
 * @returns The boolean values for the given Property.
 */
function getBooleanAll(thing, property) {
    const literalStrings = getLiteralAllOfType(thing, property, xmlSchemaTypes.boolean);
    return literalStrings
        .map(deserializeBoolean)
        .filter((possibleBoolean) => possibleBoolean !== null);
}
/**
 * @param thing The [[Thing]] to read a datetime value from.
 * @param property The given Property for which you want the datetime value.
 * @returns A datetime value for the given Property, if present, or null otherwise.
 */
function getDatetime(thing, property) {
    const literalString = getLiteralOfType(thing, property, xmlSchemaTypes.dateTime);
    if (literalString === null) {
        return null;
    }
    return deserializeDatetime(literalString);
}
/**
 * @param thing The [[Thing]] to read the datetime values from.
 * @param property The given Property for which you want the datetime values.
 * @returns The datetime values for the given Property.
 */
function getDatetimeAll(thing, property) {
    const literalStrings = getLiteralAllOfType(thing, property, xmlSchemaTypes.dateTime);
    return literalStrings
        .map(deserializeDatetime)
        .filter((potentialDatetime) => potentialDatetime !== null);
}
/**
 * @param thing The [[Thing]] to read a decimal value from.
 * @param property The given Property for which you want the decimal value.
 * @returns A decimal value for the given Property, if present, or null otherwise.
 */
function getDecimal(thing, property) {
    const literalString = getLiteralOfType(thing, property, xmlSchemaTypes.decimal);
    if (literalString === null) {
        return null;
    }
    return deserializeDecimal(literalString);
}
/**
 * @param thing The [[Thing]] to read the decimal values from.
 * @param property The given Property for which you want the decimal values.
 * @returns The decimal values for the given Property.
 */
function getDecimalAll(thing, property) {
    const literalStrings = getLiteralAllOfType(thing, property, xmlSchemaTypes.decimal);
    return literalStrings
        .map((literalString) => deserializeDecimal(literalString))
        .filter((potentialDecimal) => potentialDecimal !== null);
}
/**
 * @param thing The [[Thing]] to read an integer value from.
 * @param property The given Property for which you want the integer value.
 * @returns An integer value for the given Property, if present, or null otherwise.
 */
function getInteger(thing, property) {
    const literalString = getLiteralOfType(thing, property, xmlSchemaTypes.integer);
    if (literalString === null) {
        return null;
    }
    return deserializeInteger(literalString);
}
/**
 * @param thing The [[Thing]] to read the integer values from.
 * @param property The given Property for which you want the integer values.
 * @returns The integer values for the given Property.
 */
function getIntegerAll(thing, property) {
    const literalStrings = getLiteralAllOfType(thing, property, xmlSchemaTypes.integer);
    return literalStrings
        .map((literalString) => deserializeInteger(literalString))
        .filter((potentialInteger) => potentialInteger !== null);
}
/**
 * @param thing The [[Thing]] to read a localised string value from.
 * @param property The given Property for which you want the localised string value.
 * @param locale The desired locale for the string value.
 * @returns A localised string value for the given Property, if present in `locale`, or null otherwise.
 */
function getStringWithLocale(thing, property, locale) {
    const localeStringMatcher = getLocaleStringMatcher(property, locale);
    const matchingQuad = findOne(thing, localeStringMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object.value;
}
/**
 * @param thing The [[Thing]] to read the localised string values from.
 * @param property The given Property for which you want the localised string values.
 * @param locale The desired locale for the string values.
 * @returns The localised string values for the given Property.
 */
function getStringWithLocaleAll(thing, property, locale) {
    const localeStringMatcher = getLocaleStringMatcher(property, locale);
    const matchingQuads = findAll(thing, localeStringMatcher);
    return matchingQuads.map((quad) => quad.object.value);
}
/**
 * Retrieves all language string literals for the specified property from the
 * specified [[Thing]] (explicitly filters out non-language string literals).
 *
 * @param thing The [[Thing]] to read the localised string values from.
 * @param property The given Property for which you want the localised string values.
 * @returns A Map of objects, keyed on locale with the value an array of string values (for that locale).
 */
function getStringByLocaleAll(thing, property) {
    const literalMatcher = getLiteralMatcher(property);
    const matchingQuads = findAll(thing, literalMatcher);
    const result = new Map();
    matchingQuads.map((quad) => {
        if (quad.object.datatype.value === xmlSchemaTypes.langString) {
            const languageTag = quad.object.language;
            const current = result.get(languageTag);
            current
                ? result.set(languageTag, [...current, quad.object.value])
                : result.set(languageTag, [quad.object.value]);
        }
    });
    return result;
}
/**
 * @param thing The [[Thing]] to read a string value from.
 * @param property The given Property for which you want the string value.
 * @returns A string value for the given Property, if present, or null otherwise.
 */
function getStringNoLocale(thing, property) {
    const literalString = getLiteralOfType(thing, property, xmlSchemaTypes.string);
    return literalString;
}
/**
 * @param thing The [[Thing]] to read the string values from.
 * @param property The given Property for which you want the string values.
 * @returns The string values for the given Property.
 */
function getStringNoLocaleAll(thing, property) {
    const literalStrings = getLiteralAllOfType(thing, property, xmlSchemaTypes.string);
    return literalStrings;
}
/**
 * @param thing The [[Thing]] to read a NamedNode value from.
 * @param property The given Property for which you want the NamedNode value.
 * @returns A NamedNode value for the given Property, if present, or null otherwise.
 * @ignore This should not be needed due to the other get*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/#namednode-interface
 */
function getNamedNode(thing, property) {
    const namedNodeMatcher = getNamedNodeMatcher(property);
    const matchingQuad = findOne(thing, namedNodeMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object;
}
/**
 * @param thing The [[Thing]] to read the NamedNode values from.
 * @param property The given Property for which you want the NamedNode values.
 * @returns The NamedNode values for the given Property.
 * @ignore This should not be needed due to the other get*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/#namednode-interface
 */
function getNamedNodeAll(thing, property) {
    const namedNodeMatcher = getNamedNodeMatcher(property);
    const matchingQuads = findAll(thing, namedNodeMatcher);
    return matchingQuads.map((quad) => quad.object);
}
/**
 * @param thing The [[Thing]] to read a Literal value from.
 * @param property The given Property for which you want the Literal value.
 * @returns A Literal value for the given Property, if present, or null otherwise.
 * @ignore This should not be needed due to the other get*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/#literal-interface
 */
function getLiteral(thing, property) {
    const literalMatcher = getLiteralMatcher(property);
    const matchingQuad = findOne(thing, literalMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object;
}
/**
 * @param thing The [[Thing]] to read the Literal values from.
 * @param property The given Property for which you want the Literal values.
 * @returns The Literal values for the given Property.
 * @ignore This should not be needed due to the other get*All() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/#literal-interface
 */
function getLiteralAll(thing, property) {
    const literalMatcher = getLiteralMatcher(property);
    const matchingQuads = findAll(thing, literalMatcher);
    return matchingQuads.map((quad) => quad.object);
}
/**
 * @param thing The [[Thing]] to read a raw RDF/JS value from.
 * @param property The given Property for which you want the raw value.
 * @returns A Term for the given Property, if present, or null otherwise.
 * @ignore This should not be needed due to the other get*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/
 * @since 0.3.0
 */
function getTerm(thing, property) {
    const termMatcher = getTermMatcher(property);
    const matchingQuad = findOne(thing, termMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object;
}
/**
 * @param thing The [[Thing]] to read the raw RDF/JS values from.
 * @param property The given Property for which you want the raw values.
 * @returns The Terms for the given Property.
 * @ignore This should not be needed due to the other get*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @see https://rdf.js.org/data-model-spec/
 * @since 0.3.0
 */
function getTermAll(thing, property) {
    const namedNodeMatcher = getTermMatcher(property);
    const matchingQuads = findAll(thing, namedNodeMatcher);
    return matchingQuads.map((quad) => quad.object);
}
/**
 * @param thing The [[Thing]] to extract a Quad from.
 * @param matcher Callback function that returns a boolean indicating whether a given Quad should be included.
 * @returns First Quad in `thing` for which `matcher` returned true.
 */
function findOne(thing, matcher) {
    for (const quad of thing) {
        if (matcher(quad)) {
            return quad;
        }
    }
    return null;
}
/**
 * @param thing The [[Thing]] to extract Quads from.
 * @param matcher Callback function that returns a boolean indicating whether a given Quad should be included.
 * @returns All Quads in `thing` for which `matcher` returned true.
 */
function findAll(thing, matcher) {
    const matched = [];
    for (const quad of thing) {
        if (matcher(quad)) {
            matched.push(quad);
        }
    }
    return matched;
}
function getNamedNodeMatcher(property) {
    const predicateNode = asNamedNode(property);
    const matcher = function matcher(quad) {
        return predicateNode.equals(quad.predicate) && isNamedNode(quad.object);
    };
    return matcher;
}
function getLiteralMatcher(property) {
    const predicateNode = asNamedNode(property);
    const matcher = function matcher(quad) {
        return predicateNode.equals(quad.predicate) && isLiteral(quad.object);
    };
    return matcher;
}
function getTermMatcher(property) {
    const predicateNode = asNamedNode(property);
    const matcher = function matcher(quad) {
        return predicateNode.equals(quad.predicate) && isTerm(quad.object);
    };
    return matcher;
}
function getLiteralOfTypeMatcher(property, datatype) {
    const predicateNode = asNamedNode(property);
    const matcher = function matcher(quad) {
        return (predicateNode.equals(quad.predicate) &&
            isLiteral(quad.object) &&
            quad.object.datatype.value === datatype);
    };
    return matcher;
}
function getLocaleStringMatcher(property, locale) {
    const predicateNode = asNamedNode(property);
    const matcher = function matcher(quad) {
        return (predicateNode.equals(quad.predicate) &&
            isLiteral(quad.object) &&
            quad.object.datatype.value === xmlSchemaTypes.langString &&
            quad.object.language.toLowerCase() === locale.toLowerCase());
    };
    return matcher;
}
/**
 * @param thing The [Thing]] to read a Literal of the given type from.
 * @param property The given Property for which you want the Literal value.
 * @param literalType Set type of the Literal data.
 * @returns The stringified value for the given Property and type, if present, or null otherwise.
 */
function getLiteralOfType(thing, property, literalType) {
    const literalOfTypeMatcher = getLiteralOfTypeMatcher(property, literalType);
    const matchingQuad = findOne(thing, literalOfTypeMatcher);
    if (matchingQuad === null) {
        return null;
    }
    return matchingQuad.object.value;
}
/**
 * @param thing The [Thing]] to read the Literals of the given type from.
 * @param property The given Property for which you want the Literal values.
 * @param literalType Set type of the Literal data.
 * @returns The stringified values for the given Property and type.
 */
function getLiteralAllOfType(thing, property, literalType) {
    const literalOfTypeMatcher = getLiteralOfTypeMatcher(property, literalType);
    const matchingQuads = findAll(thing, literalOfTypeMatcher);
    return matchingQuads.map((quad) => quad.object.value);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Extract Quads with a given Subject from a [[SolidDataset]] into a [[Thing]].
 *
 * @param solidDataset The [[SolidDataset]] to extract the [[Thing]] from.
 * @param thingUrl The URL of the desired [[Thing]].
 * @param options Not yet implemented.
 */
function getThing(solidDataset, thingUrl, options = {}) {
    const subject = isLocalNode(thingUrl) ? thingUrl : asNamedNode(thingUrl);
    const scope = options.scope
        ? asNamedNode(options.scope)
        : null;
    const thingDataset = solidDataset.match(subject, null, null, scope);
    if (thingDataset.size === 0) {
        return null;
    }
    if (isLocalNode(subject)) {
        const thing = Object.assign(thingDataset, {
            internal_localSubject: subject,
        });
        return thing;
    }
    else {
        const thing = Object.assign(thingDataset, {
            internal_url: subject.value,
        });
        return thing;
    }
}
/**
 * Get all [[Thing]]s about which a [[SolidDataset]] contains Quads.
 *
 * @param solidDataset The [[SolidDataset]] to extract the [[Thing]]s from.
 * @param options Not yet implemented.
 */
function getThingAll(solidDataset, options = {}) {
    const subjectNodes = new Array();
    for (const quad of solidDataset) {
        // Because NamedNode objects with the same IRI are actually different
        // object instances, we have to manually check whether `subjectNodes` does
        // not yet include `quadSubject` before adding it.
        const quadSubject = quad.subject;
        if (isNamedNode(quadSubject) &&
            !subjectNodes.some((subjectNode) => isEqual(subjectNode, quadSubject))) {
            subjectNodes.push(quadSubject);
        }
        if (isLocalNode(quadSubject) &&
            !subjectNodes.some((subjectNode) => isEqual(subjectNode, quadSubject))) {
            subjectNodes.push(quadSubject);
        }
    }
    const things = subjectNodes.map((subjectNode) => getThing(solidDataset, subjectNode, options)
    // We can make the type assertion here because `getThing` only returns `null` if no data with
    // the given subject node can be found, and in this case the subject node was extracted from
    // existing data (i.e. that can be found by definition):
    );
    return things;
}
/**
 * Insert a [[Thing]] into a [[SolidDataset]], replacing previous instances of that Thing.
 *
 * @param solidDataset The SolidDataset to insert a Thing into.
 * @param thing The Thing to insert into the given SolidDataset.
 * @returns A new SolidDataset equal to the given SolidDataset, but with the given Thing.
 */
function setThing(solidDataset, thing) {
    const newDataset = removeThing(solidDataset, thing);
    newDataset.internal_changeLog = {
        additions: [...newDataset.internal_changeLog.additions],
        deletions: [...newDataset.internal_changeLog.deletions],
    };
    for (const quad of thing) {
        newDataset.add(quad);
        if (newDataset.internal_changeLog.deletions.includes(quad)) {
            newDataset.internal_changeLog.deletions = newDataset.internal_changeLog.deletions.filter((deletion) => deletion !== quad);
        }
        else {
            newDataset.internal_changeLog.additions.push(quad);
        }
    }
    return newDataset;
}
/**
 * Remove a Thing from a SolidDataset.
 *
 * @param solidDataset The SolidDataset to remove a Thing from.
 * @param thing The Thing to remove from `solidDataset`.
 * @returns A new [[SolidDataset]] equal to the input SolidDataset, excluding the given Thing.
 */
function removeThing(solidDataset, thing) {
    const newSolidDataset = withChangeLog(internal_cloneResource(solidDataset));
    newSolidDataset.internal_changeLog = {
        additions: [...newSolidDataset.internal_changeLog.additions],
        deletions: [...newSolidDataset.internal_changeLog.deletions],
    };
    const resourceIri = hasResourceInfo(newSolidDataset)
        ? getSourceUrl(newSolidDataset)
        : undefined;
    const thingSubject = internal_toNode(thing);
    const existingQuads = Array.from(newSolidDataset);
    existingQuads.forEach((quad) => {
        if (!isNamedNode(quad.subject) && !isLocalNode(quad.subject)) {
            // This data is unexpected, and hence unlikely to be added by us. Thus, leave it intact:
            return;
        }
        if (isEqual(thingSubject, quad.subject, { resourceIri: resourceIri })) {
            newSolidDataset.delete(quad);
            if (newSolidDataset.internal_changeLog.additions.includes(quad)) {
                newSolidDataset.internal_changeLog.additions = newSolidDataset.internal_changeLog.additions.filter((addition) => addition !== quad);
            }
            else {
                newSolidDataset.internal_changeLog.deletions.push(quad);
            }
        }
    });
    return newSolidDataset;
}
function withChangeLog(solidDataset) {
    const newSolidDataset = hasChangelog(solidDataset)
        ? solidDataset
        : Object.assign(internal_cloneResource(solidDataset), {
            internal_changeLog: { additions: [], deletions: [] },
        });
    return newSolidDataset;
}
function createThing(options = {}) {
    var _a;
    if (typeof options.url !== "undefined") {
        const url = options.url;
        /* istanbul ignore else [URL is defined is the testing environment, so we cannot test this] */
        if (typeof URL !== "undefined") {
            // Throws an error if the IRI is invalid:
            new URL(url);
        }
        const thing = Object.assign(dataset(), {
            internal_url: url,
        });
        return thing;
    }
    const name = (_a = options.name) !== null && _a !== void 0 ? _a : generateName();
    const localSubject = getLocalNode(name);
    const thing = Object.assign(dataset(), {
        internal_localSubject: localSubject,
    });
    return thing;
}
/**
 * @param input An value that might be a [[Thing]].
 * @returns Whether `input` is a Thing.
 * @since 0.2.0
 */
function isThing(input) {
    return (internal_isDatasetCore(input) &&
        (isThingLocal(input) ||
            typeof input.internal_url === "string"));
}
function asUrl(thing, baseUrl) {
    if (isThingLocal(thing)) {
        if (typeof baseUrl === "undefined") {
            throw new Error("The URL of a Thing that has not been persisted cannot be determined without a base URL.");
        }
        return resolveLocalIri(thing.internal_localSubject.internal_name, baseUrl);
    }
    return thing.internal_url;
}
/** @hidden Alias of [[asUrl]] for those who prefer IRI terminology. */
const asIri = asUrl;
/**
 * Gets a human-readable representation of the given Thing to aid debugging.
 *
 * Note that changes to the exact format of the return value are not considered a breaking change;
 * it is intended to aid in debugging, not as a serialisation method that can be reliably parsed.
 *
 * @param thing The Thing to get a human-readable representation of.
 * @since 0.3.0
 */
function thingAsMarkdown(thing) {
    let thingAsMarkdown = "";
    if (isThingLocal(thing)) {
        thingAsMarkdown += `## Thing (no URL yet — identifier: \`#${thing.internal_localSubject.internal_name}\`)\n`;
    }
    else {
        thingAsMarkdown += `## Thing: ${thing.internal_url}\n`;
    }
    const quads = Array.from(thing);
    if (quads.length === 0) {
        thingAsMarkdown += "\n<empty>\n";
    }
    else {
        const predicates = new Set(quads.map((quad) => quad.predicate.value));
        for (const predicate of predicates) {
            thingAsMarkdown += `\nProperty: ${predicate}\n`;
            const values = getTermAll(thing, predicate);
            values.forEach((value) => {
                thingAsMarkdown += `- ${internal_getReadableValue(value)}\n`;
            });
        }
    }
    return thingAsMarkdown;
}
/** @hidden For internal use only. */
function internal_getReadableValue(value) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (isNamedNode(value)) {
        return `<${value.value}> (URL)`;
    }
    if (isLiteral(value)) {
        if (!isNamedNode(value.datatype)) {
            return `[${value.value}] (RDF/JS Literal of unknown type)`;
        }
        let val;
        switch (value.datatype.value) {
            case xmlSchemaTypes.boolean:
                val = (_b = (_a = deserializeBoolean(value.value)) === null || _a === void 0 ? void 0 : _a.valueOf()) !== null && _b !== void 0 ? _b : `Invalid data: \`${value.value}\``;
                return val + " (boolean)";
            case xmlSchemaTypes.dateTime:
                val = (_d = (_c = deserializeDatetime(value.value)) === null || _c === void 0 ? void 0 : _c.toUTCString()) !== null && _d !== void 0 ? _d : `Invalid data: \`${value.value}\``;
                return val + " (datetime)";
            case xmlSchemaTypes.decimal:
                val = (_f = (_e = deserializeDecimal(value.value)) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : `Invalid data: \`${value.value}\``;
                return val + " (decimal)";
            case xmlSchemaTypes.integer:
                val = (_h = (_g = deserializeInteger(value.value)) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : `Invalid data: \`${value.value}\``;
                return val + " (integer)";
            case xmlSchemaTypes.langString:
                return `"${value.value}" (${value.language} string)`;
            case xmlSchemaTypes.string:
                return `"${value.value}" (string)`;
            default:
                return `[${value.value}] (RDF/JS Literal of type: \`${value.datatype.value}\`)`;
        }
    }
    if (isLocalNode(value)) {
        return `<#${value.internal_name}> (URL)`;
    }
    if (value.termType === "BlankNode") {
        return `[${value.value}] (RDF/JS BlankNode)`;
    }
    if (value.termType === "Quad") {
        return `??? (nested RDF* Quad)`;
    }
    /* istanbul ignore else: The if statements are exhaustive; if not, TypeScript will complain. */
    if (value.termType === "Variable") {
        return `?${value.value} (RDF/JS Variable)`;
    }
    /* istanbul ignore next: The if statements are exhaustive; if not, TypeScript will complain. */
    return value;
}
/**
 * @param thing The [[Thing]] of which a URL might or might not be known.
 * @return Whether `thing` has no known URL yet.
 */
function isThingLocal(thing) {
    var _a;
    return (typeof ((_a = thing.internal_localSubject) === null || _a === void 0 ? void 0 : _a.internal_name) ===
        "string" && typeof thing.internal_url === "undefined");
}
function internal_toNode(thing) {
    if (isNamedNode(thing) || isLocalNode(thing)) {
        return thing;
    }
    if (typeof thing === "string") {
        return asNamedNode(thing);
    }
    if (isThingLocal(thing)) {
        return thing.internal_localSubject;
    }
    return asNamedNode(asUrl(thing));
}
/**
 * @internal
 * @param thing Thing to clone.
 * @returns A new Thing with the same Quads as `input`.
 */
function cloneThing(thing) {
    const cloned = clone(thing);
    if (isThingLocal(thing)) {
        cloned.internal_localSubject = thing.internal_localSubject;
        return cloned;
    }
    cloned.internal_url = thing.internal_url;
    return cloned;
}
/**
 * @internal
 * @param thing Thing to clone.
 * @param callback Function that takes a Quad, and returns a boolean indicating whether that Quad should be included in the cloned Dataset.
 * @returns A new Thing with the same Quads as `input`, excluding the ones for which `callback` returned `false`.
 */
function filterThing(thing, callback) {
    const filtered = filter(thing, callback);
    if (isThingLocal(thing)) {
        filtered.internal_localSubject =
            thing.internal_localSubject;
        return filtered;
    }
    filtered.internal_url = thing.internal_url;
    return filtered;
}
/**
 * Generate a string that can be used as the unique identifier for a Thing
 *
 * This function works by starting with a date string (so that Things can be
 * sorted chronologically), followed by a random number generated by taking a
 * random number between 0 and 1, and cutting off the `0.`.
 *
 * @internal
 * @returns An string that's likely to be unique
 */
const generateName = () => {
    return (Date.now().toString() + Math.random().toString().substring("0.".length));
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Initialise a new [[SolidDataset]] in memory.
 *
 * @returns An empty [[SolidDataset]].
 */
function createSolidDataset() {
    return dataset();
}
/**
 * Fetch a SolidDataset from the given URL. Currently requires the SolidDataset to be available as [Turtle](https://www.w3.org/TR/turtle/).
 *
 * @param url URL to fetch a [[SolidDataset]] from.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns Promise resolving to a [[SolidDataset]] containing the data at the given Resource, or rejecting if fetching it failed.
 */
function getSolidDataset(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        url = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const response = yield config.fetch(url, {
            headers: {
                Accept: "text/turtle",
            },
        });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Fetching the Resource at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const data = yield response.text();
        const triples = yield turtleToTriples(data, url);
        const resource = dataset();
        triples.forEach((triple) => resource.add(triple));
        const resourceInfo = internal_parseResourceInfo(response);
        const resourceWithResourceInfo = Object.assign(resource, {
            internal_resourceInfo: resourceInfo,
        });
        return resourceWithResourceInfo;
    });
}
/**
 * Experimental: fetch a SolidDataset and its associated Access Control List.
 *
 * This is an experimental function that fetches both a Resource, the linked ACL Resource (if
 * available), and the ACL that applies to it if the linked ACL Resource is not available. This can
 * result in many HTTP requests being executed, in lieu of the Solid spec mandating servers to
 * provide this info in a single request. Therefore, and because this function is still
 * experimental, prefer [[getSolidDataset]] instead.
 *
 * If the Resource does not advertise the ACL Resource (because the authenticated user does not have
 * access to it), the `acl` property in the returned value will be null. `acl.resourceAcl` will be
 * undefined if the Resource's linked ACL Resource could not be fetched (because it does not exist),
 * and `acl.fallbackAcl` will be null if the applicable Container's ACL is not accessible to the
 * authenticated user.
 *
 * @param url URL of the SolidDataset to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A SolidDataset and the ACLs that apply to it, if available to the authenticated user.
 */
function getSolidDatasetWithAcl(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const solidDataset = yield getSolidDataset(url, options);
        const acl = yield internal_fetchAcl(solidDataset, options);
        return Object.assign(solidDataset, { internal_acl: acl });
    });
}
/**
 * Create a SPARQL UPDATE Patch request from a [[SolidDataset]] with a changelog.
 * @param solidDataset the [[SolidDataset]] that has been locally updated, and that should be persisted.
 * @returns an HTTP PATCH request configuration object, aligned with the [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters), containing a SPARQL UPDATE.
 * @hidden
 */
function prepareSolidDatasetUpdate(solidDataset) {
    return __awaiter(this, void 0, void 0, function* () {
        const deleteStatement = solidDataset.internal_changeLog.deletions.length > 0
            ? `DELETE DATA {${(yield triplesToTurtle(solidDataset.internal_changeLog.deletions.map(getNamedNodesForLocalNodes))).trim()}};`
            : "";
        const insertStatement = solidDataset.internal_changeLog.additions.length > 0
            ? `INSERT DATA {${(yield triplesToTurtle(solidDataset.internal_changeLog.additions.map(getNamedNodesForLocalNodes))).trim()}};`
            : "";
        return {
            method: "PATCH",
            body: `${deleteStatement} ${insertStatement}`,
            headers: {
                "Content-Type": "application/sparql-update",
            },
        };
    });
}
/**
 * Create a Put request to write a locally created [[SolidDataset]] to a Pod.
 * @param solidDataset the [[SolidDataset]] that has been locally updated, and that should be persisted.
 * @returns an HTTP PUT request configuration object, aligned with the [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters), containing a serialization of the [[SolidDataset]].
 * @hidden
 */
function prepareSolidDatasetCreation(solidDataset) {
    return __awaiter(this, void 0, void 0, function* () {
        return {
            method: "PUT",
            body: yield triplesToTurtle(Array.from(solidDataset).map(getNamedNodesForLocalNodes)),
            headers: {
                "Content-Type": "text/turtle",
                "If-None-Match": "*",
                Link: '<http://www.w3.org/ns/ldp#Resource>; rel="type"',
            },
        };
    });
}
/**
 * Given a SolidDataset, store it in a Solid Pod (overwriting the existing data at the given URL).
 *
 * A SolidDataset keeps track of the data changes compared to the data in the Pod; i.e.,
 * the changelog tracks both the old value and new values of the property being modified. This
 * function applies the changes to the current SolidDataset. If the old value specified in the
 * changelog does not correspond to the value currently in the Pod, this function will throw an
 * error.
 * The SolidDataset returned by this function will contain the data sent to the Pod, and a ChangeLog
 * up-to-date with the saved data. Note that if the data on the server was modified in between the
 * first fetch and saving it, the updated data will not be reflected in the returned SolidDataset.
 * To make sure you have the latest data, call [[getSolidDataset]] again after saving the data.
 *
 * @param url URL to save `solidDataset` to.
 * @param solidDataset The [[SolidDataset]] to save.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A Promise resolving to a [[SolidDataset]] containing the stored data, or rejecting if saving it failed.
 */
function saveSolidDatasetAt(url, solidDataset, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        url = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const requestInit = isUpdate(solidDataset, url)
            ? yield prepareSolidDatasetUpdate(solidDataset)
            : yield prepareSolidDatasetCreation(solidDataset);
        const response = yield config.fetch(url, requestInit);
        if (internal_isUnsuccessfulResponse(response)) {
            const diagnostics = isUpdate(solidDataset, url)
                ? "The changes that were sent to the Pod are listed below.\n\n" +
                    changeLogAsMarkdown(solidDataset)
                : "The SolidDataset that was sent to the Pod is listed below.\n\n" +
                    solidDatasetAsMarkdown(solidDataset);
            throw new FetchError(`Storing the Resource at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.\n\n` +
                diagnostics, response);
        }
        const resourceInfo = Object.assign(Object.assign({}, internal_parseResourceInfo(response)), { sourceIri: url, isRawData: false });
        const storedDataset = Object.assign(internal_cloneResource(solidDataset), {
            internal_changeLog: { additions: [], deletions: [] },
            internal_resourceInfo: resourceInfo,
        });
        const storedDatasetWithResolvedIris = resolveLocalIrisInSolidDataset(storedDataset);
        return storedDatasetWithResolvedIris;
    });
}
/**
 * Deletes the SolidDataset at a given URL.
 *
 * @param file The (URL of the) SolidDataset to delete
 * @since 0.6.0
 */
function deleteSolidDataset(solidDataset, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const url = hasResourceInfo(solidDataset)
            ? internal_toIriString(getSourceUrl(solidDataset))
            : internal_toIriString(solidDataset);
        const response = yield config.fetch(url, { method: "DELETE" });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Deleting the SolidDataset at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
    });
}
/**
 * Create an empty Container at the given URL.
 *
 * Throws an error if creating the Container failed, e.g. because the current user does not have
 * permissions to, or because the Container already exists.
 *
 * @param url URL of the empty Container that is to be created.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @since 0.2.0
 */
function createContainerAt(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        url = internal_toIriString(url);
        url = url.endsWith("/") ? url : url + "/";
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const response = yield config.fetch(url, {
            method: "PUT",
            headers: {
                Accept: "text/turtle",
                "Content-Type": "text/turtle",
                "If-None-Match": "*",
                // This header should not be required to create a Container,
                // but ESS currently expects it:
                Link: '<http://www.w3.org/ns/ldp#BasicContainer>; rel="type"',
            },
        });
        if (internal_isUnsuccessfulResponse(response)) {
            if (response.status === 409 &&
                response.statusText === "Conflict" &&
                (yield response.text()).trim() ===
                    internal_NSS_CREATE_CONTAINER_SPEC_NONCOMPLIANCE_DETECTION_ERROR_MESSAGE_TO_WORKAROUND_THEIR_ISSUE_1465) {
                return createContainerWithNssWorkaroundAt(url, options);
            }
            throw new FetchError(`Creating the empty Container at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const resourceInfo = internal_parseResourceInfo(response);
        const containerDataset = Object.assign(dataset(), {
            internal_changeLog: { additions: [], deletions: [] },
            internal_resourceInfo: resourceInfo,
        });
        return containerDataset;
    });
}
/**
 * Unfortunately Node Solid Server does not confirm to the Solid spec when it comes to Container
 * creation. When we make the (valid, according to the Solid protocol) request to create a
 * Container, NSS responds with the following exact error message. Thus, when we encounter exactly
 * this message, we use an NSS-specific workaround ([[createContainerWithNssWorkaroundAt]]). Both
 * this constant and that workaround should be removed once the NSS issue has been fixed and
 * no versions of NSS with the issue are in common use/supported anymore.
 *
 * @see https://github.com/solid/node-solid-server/issues/1465
 * @internal
 */
const internal_NSS_CREATE_CONTAINER_SPEC_NONCOMPLIANCE_DETECTION_ERROR_MESSAGE_TO_WORKAROUND_THEIR_ISSUE_1465 = "Can't write file: PUT not supported on containers, use POST instead";
/**
 * Unfortunately Node Solid Server does not confirm to the Solid spec when it comes to Container
 * creation. As a workaround, we create a dummy file _inside_ the desired Container (which should
 * create the desired Container on the fly), and then delete it again.
 *
 * @see https://github.com/solid/node-solid-server/issues/1465
 */
const createContainerWithNssWorkaroundAt = (url, options) => __awaiter(void 0, void 0, void 0, function* () {
    url = internal_toIriString(url);
    const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
    let existingContainer;
    try {
        existingContainer = yield getResourceInfo(url, options);
    }
    catch (e) {
        // To create the Container, we'd want it to not exist yet. In other words, we'd expect to get
        // a 404 error here in the happy path - so do nothing if that's the case.
        if (!(e instanceof FetchError) || e.statusCode !== 404) {
            // (But if we get an error other than a 404, just throw that error like we usually would.)
            throw e;
        }
    }
    if (typeof existingContainer !== "undefined") {
        throw new Error(`The Container at \`${url}\` already exists, and therefore cannot be created again.`);
    }
    const dummyUrl = url + ".dummy";
    const createResponse = yield config.fetch(dummyUrl, {
        method: "PUT",
        headers: {
            Accept: "text/turtle",
            "Content-Type": "text/turtle",
        },
    });
    if (internal_isUnsuccessfulResponse(createResponse)) {
        throw new FetchError(`Creating the empty Container at \`${url}\` failed: \`${createResponse.status}\` \`${createResponse.statusText}\`.`, createResponse);
    }
    yield config.fetch(dummyUrl, { method: "DELETE" });
    const containerInfoResponse = yield config.fetch(url, { method: "HEAD" });
    const resourceInfo = internal_parseResourceInfo(containerInfoResponse);
    const containerDataset = Object.assign(dataset(), {
        internal_changeLog: { additions: [], deletions: [] },
        internal_resourceInfo: resourceInfo,
    });
    return containerDataset;
});
function isUpdate(solidDataset, url) {
    return (hasChangelog(solidDataset) &&
        hasResourceInfo(solidDataset) &&
        typeof solidDataset.internal_resourceInfo.sourceIri === "string" &&
        solidDataset.internal_resourceInfo.sourceIri === url);
}
/**
 * Given a SolidDataset, store it in a Solid Pod in a new Resource inside a Container.
 *
 * @param containerUrl URL of the Container in which to create a new Resource.
 * @param solidDataset The [[SolidDataset]] to save to a new Resource in the given Container.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A Promise resolving to a [[SolidDataset]] containing the saved data. The Promise rejects if the save failed.
 */
function saveSolidDatasetInContainer(containerUrl, solidDataset, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        containerUrl = internal_toIriString(containerUrl);
        const rawTurtle = yield triplesToTurtle(Array.from(solidDataset).map(getNamedNodesForLocalNodes));
        const headers = {
            "Content-Type": "text/turtle",
            Link: '<http://www.w3.org/ns/ldp#Resource>; rel="type"',
        };
        if (options.slugSuggestion) {
            headers.slug = options.slugSuggestion;
        }
        const response = yield config.fetch(containerUrl, {
            method: "POST",
            body: rawTurtle,
            headers: headers,
        });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Storing the Resource in the Container at \`${containerUrl}\` failed: \`${response.status}\` \`${response.statusText}\`.\n\n` +
                "The SolidDataset that was sent to the Pod is listed below.\n\n" +
                solidDatasetAsMarkdown(solidDataset), response);
        }
        const locationHeader = response.headers.get("Location");
        if (locationHeader === null) {
            throw new Error("Could not determine the location of the newly saved SolidDataset.");
        }
        const resourceIri = new URL(locationHeader, new URL(containerUrl).origin)
            .href;
        const resourceInfo = {
            internal_resourceInfo: {
                isRawData: false,
                sourceIri: resourceIri,
            },
        };
        const resourceWithResourceInfo = Object.assign(internal_cloneResource(solidDataset), resourceInfo);
        const resourceWithResolvedIris = resolveLocalIrisInSolidDataset(resourceWithResourceInfo);
        return resourceWithResolvedIris;
    });
}
/**
 * Create an empty Container inside the Container at the given URL.
 *
 * Throws an error if creating the Container failed, e.g. because the current user does not have
 * permissions to.
 *
 * @param containerUrl URL of the Container in which the empty Container is to be created.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @since 0.2.0
 */
function createContainerInContainer(containerUrl, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        containerUrl = internal_toIriString(containerUrl);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const headers = {
            "Content-Type": "text/turtle",
            Link: '<http://www.w3.org/ns/ldp#BasicContainer>; rel="type"',
        };
        if (options.slugSuggestion) {
            headers.slug = options.slugSuggestion;
        }
        const response = yield config.fetch(containerUrl, {
            method: "POST",
            headers: headers,
        });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Creating an empty Container in the Container at \`${containerUrl}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const locationHeader = response.headers.get("Location");
        if (locationHeader === null) {
            throw new Error("Could not determine the location of the newly created Container.");
        }
        const resourceIri = new URL(locationHeader, new URL(containerUrl).origin)
            .href;
        const resourceInfo = {
            internal_resourceInfo: {
                isRawData: false,
                sourceIri: resourceIri,
            },
        };
        const resourceWithResourceInfo = Object.assign(dataset(), resourceInfo);
        return resourceWithResourceInfo;
    });
}
/**
 * Deletes the Container at a given URL.
 *
 * @param file The (URL of the) Container to delete
 * @since 0.6.0
 */
function deleteContainer(container, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = hasResourceInfo(container)
            ? internal_toIriString(getSourceUrl(container))
            : internal_toIriString(container);
        if (!isContainer(container)) {
            throw new Error(`You're trying to delete the Container at \`${url}\`, but Container URLs should end in a \`/\`. Are you sure this is a Container?`);
        }
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const response = yield config.fetch(url, { method: "DELETE" });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Deleting the Container at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
    });
}
/**
 * Gets a human-readable representation of the given SolidDataset to aid debugging.
 *
 * Note that changes to the exact format of the return value are not considered a breaking change;
 * it is intended to aid in debugging, not as a serialisation method that can be reliably parsed.
 *
 * @param solidDataset The [[SolidDataset]] to get a human-readable representation of.
 * @since 0.3.0
 */
function solidDatasetAsMarkdown(solidDataset) {
    let readableSolidDataset = "";
    if (hasResourceInfo(solidDataset)) {
        readableSolidDataset += `# SolidDataset: ${solidDataset.internal_resourceInfo.sourceIri}\n`;
    }
    else {
        readableSolidDataset += `# SolidDataset (no URL yet)\n`;
    }
    const things = getThingAll(solidDataset);
    if (things.length === 0) {
        readableSolidDataset += "\n<empty>\n";
    }
    else {
        things.forEach((thing) => {
            readableSolidDataset += "\n" + thingAsMarkdown(thing);
            if (hasChangelog(solidDataset)) {
                readableSolidDataset +=
                    "\n" + getReadableChangeLogSummary(solidDataset, thing) + "\n";
            }
        });
    }
    return readableSolidDataset;
}
/**
 * Gets a human-readable representation of the local changes to a Resource to aid debugging.
 *
 * Note that changes to the exact format of the return value are not considered a breaking change;
 * it is intended to aid in debugging, not as a serialisation method that can be reliably parsed.
 *
 * @param solidDataset The Resource of which to get a human-readable representation of the changes applied to it locally.
 * @since 0.3.0
 */
function changeLogAsMarkdown(solidDataset) {
    if (!hasResourceInfo(solidDataset)) {
        return "This is a newly initialized SolidDataset, so there is no source to compare it to.";
    }
    if (!hasChangelog(solidDataset) ||
        (solidDataset.internal_changeLog.additions.length === 0 &&
            solidDataset.internal_changeLog.deletions.length === 0)) {
        return (`## Changes compared to ${getSourceUrl(solidDataset)}\n\n` +
            `This SolidDataset has not been modified since it was fetched from ${getSourceUrl(solidDataset)}.\n`);
    }
    let readableChangeLog = `## Changes compared to ${getSourceUrl(solidDataset)}\n`;
    const changeLogsByThingAndProperty = sortChangeLogByThingAndProperty(solidDataset);
    Object.keys(changeLogsByThingAndProperty).forEach((thingUrl) => {
        readableChangeLog += `\n### Thing: ${thingUrl}\n`;
        const changeLogByProperty = changeLogsByThingAndProperty[thingUrl];
        Object.keys(changeLogByProperty).forEach((propertyUrl) => {
            readableChangeLog += `\nProperty: ${propertyUrl}\n`;
            const deleted = changeLogByProperty[propertyUrl].deleted;
            const added = changeLogByProperty[propertyUrl].added;
            if (deleted.length > 0) {
                readableChangeLog += "- Removed:\n";
                deleted.forEach((deletedValue) => (readableChangeLog += `  - ${internal_getReadableValue(deletedValue)}\n`));
            }
            if (added.length > 0) {
                readableChangeLog += "- Added:\n";
                added.forEach((addedValue) => (readableChangeLog += `  - ${internal_getReadableValue(addedValue)}\n`));
            }
        });
    });
    return readableChangeLog;
}
function sortChangeLogByThingAndProperty(solidDataset) {
    const changeLogsByThingAndProperty = {};
    solidDataset.internal_changeLog.deletions.forEach((deletion) => {
        var _a, _b;
        var _c;
        const subjectNode = isLocalNode(deletion.subject)
            ? resolveIriForLocalNode(deletion.subject, getSourceUrl(solidDataset))
            : deletion.subject;
        if (!isNamedNode(subjectNode) || !isNamedNode(deletion.predicate)) {
            return;
        }
        const thingUrl = internal_toIriString(subjectNode);
        const propertyUrl = internal_toIriString(deletion.predicate);
        (_a = changeLogsByThingAndProperty[thingUrl]) !== null && _a !== void 0 ? _a : (changeLogsByThingAndProperty[thingUrl] = {});
        (_b = (_c = changeLogsByThingAndProperty[thingUrl])[propertyUrl]) !== null && _b !== void 0 ? _b : (_c[propertyUrl] = {
            added: [],
            deleted: [],
        });
        changeLogsByThingAndProperty[thingUrl][propertyUrl].deleted.push(deletion.object);
    });
    solidDataset.internal_changeLog.additions.forEach((addition) => {
        var _a, _b;
        var _c;
        const subjectNode = isLocalNode(addition.subject)
            ? resolveIriForLocalNode(addition.subject, getSourceUrl(solidDataset))
            : addition.subject;
        if (!isNamedNode(subjectNode) || !isNamedNode(addition.predicate)) {
            return;
        }
        const thingUrl = internal_toIriString(subjectNode);
        const propertyUrl = internal_toIriString(addition.predicate);
        (_a = changeLogsByThingAndProperty[thingUrl]) !== null && _a !== void 0 ? _a : (changeLogsByThingAndProperty[thingUrl] = {});
        (_b = (_c = changeLogsByThingAndProperty[thingUrl])[propertyUrl]) !== null && _b !== void 0 ? _b : (_c[propertyUrl] = {
            added: [],
            deleted: [],
        });
        changeLogsByThingAndProperty[thingUrl][propertyUrl].added.push(addition.object);
    });
    return changeLogsByThingAndProperty;
}
function getReadableChangeLogSummary(solidDataset, thing) {
    const subject = internal_toNode(thing);
    const nrOfAdditions = solidDataset.internal_changeLog.additions.reduce((count, addition) => (addition.subject.equals(subject) ? count + 1 : count), 0);
    const nrOfDeletions = solidDataset.internal_changeLog.deletions.reduce((count, deletion) => (deletion.subject.equals(subject) ? count + 1 : count), 0);
    const additionString = nrOfAdditions === 1
        ? "1 new value added"
        : nrOfAdditions + " new values added";
    const deletionString = nrOfDeletions === 1 ? "1 value removed" : nrOfDeletions + " values removed";
    return `(${additionString} / ${deletionString})`;
}
function getNamedNodesForLocalNodes(quad) {
    const subject = isLocalNode(quad.subject)
        ? getNamedNodeFromLocalNode(quad.subject)
        : quad.subject;
    const object = isLocalNode(quad.object)
        ? getNamedNodeFromLocalNode(quad.object)
        : quad.object;
    return Object.assign(Object.assign({}, quad), { subject: subject, object: object });
}
function getNamedNodeFromLocalNode(localNode) {
    return DataFactory.namedNode("#" + localNode.internal_name);
}
function resolveLocalIrisInSolidDataset(solidDataset) {
    const resourceIri = getSourceUrl(solidDataset);
    const unresolvedQuads = Array.from(solidDataset);
    unresolvedQuads.forEach((unresolvedQuad) => {
        const resolvedQuad = resolveIriForLocalNodes(unresolvedQuad, resourceIri);
        solidDataset.delete(unresolvedQuad);
        solidDataset.add(resolvedQuad);
    });
    return solidDataset;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function removeAll(thing, property) {
    const predicateNode = asNamedNode(property);
    const updatedThing = filterThing(thing, (quad) => !quad.predicate.equals(predicateNode));
    return updatedThing;
}
/**
 * Create a new Thing with the given URL removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove a URL value from.
 * @param property Property for which to remove the given URL value.
 * @param value URL to remove from `thing` for the given `Property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeUrl = (thing, property, value) => {
    const predicateNode = asNamedNode(property);
    const iriNode = isNamedNode(value)
        ? value
        : typeof value === "string"
            ? asNamedNode(value)
            : asNamedNode(asIri(value));
    const updatedThing = filterThing(thing, (quad) => {
        return (!quad.predicate.equals(predicateNode) ||
            !isNamedNode(quad.object) ||
            !quad.object.equals(iriNode));
    });
    return updatedThing;
};
/** @hidden Alias of [[removeUrl]] for those who prefer IRI terminology. */
const removeIri = removeUrl;
/**
 * Create a new Thing with the given boolean removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove a boolean value from.
 * @param property Property for which to remove the given boolean value.
 * @param value Boolean to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeBoolean = (thing, property, value) => {
    return removeLiteralMatching(thing, property, xmlSchemaTypes.boolean, (foundBoolean) => deserializeBoolean(foundBoolean) === value);
};
/**
 * Create a new Thing with the given datetime removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove a datetime value from.
 * @param property Property for which to remove the given datetime value.
 * @param value Datetime to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeDatetime = (thing, property, value) => {
    return removeLiteralMatching(thing, property, xmlSchemaTypes.dateTime, (foundDatetime) => { var _a; return ((_a = deserializeDatetime(foundDatetime)) === null || _a === void 0 ? void 0 : _a.getTime()) === value.getTime(); });
};
/**
 * Create a new Thing with the given decimal removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove a decimal value from.
 * @param property Property for which to remove the given decimal value.
 * @param value Decimal to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeDecimal = (thing, property, value) => {
    return removeLiteralMatching(thing, property, xmlSchemaTypes.decimal, (foundDecimal) => deserializeDecimal(foundDecimal) === value);
};
/**
 * Create a new Thing with the given integer removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove an integer value from.
 * @param property Property for which to remove the given integer value.
 * @param value Integer to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeInteger = (thing, property, value) => {
    return removeLiteralMatching(thing, property, xmlSchemaTypes.integer, (foundInteger) => deserializeInteger(foundInteger) === value);
};
/**
 * Create a new Thing with the given localised string removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove a localised string value from.
 * @param property Property for which to remove the given localised string value.
 * @param value String to remove from `thing` for the given `property`.
 * @param locale Locale of the string to remove.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
function removeStringWithLocale(thing, property, value, locale) {
    // Note: Due to how the `DataFactory.literal` constructor behaves, this function
    // must call directly `removeLiteral` directly, with the locale as the data
    // type of the Literal (which is not a valid NamedNode).
    return removeLiteral(thing, property, DataFactory.literal(value, normalizeLocale(locale)));
}
/**
 * Create a new Thing with the given unlocalised string removed for the given Property.
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to remove an unlocalised string value from.
 * @param property Property for which to remove the given string value.
 * @param value String to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
const removeStringNoLocale = (thing, property, value) => {
    return removeLiteralMatching(thing, property, xmlSchemaTypes.string, (foundString) => foundString === value);
};
/**
 * @ignore This should not be needed due to the other remove*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing Thing to remove a NamedNode value from.
 * @param property Property for which to remove the given NamedNode value.
 * @param value NamedNode to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
function removeNamedNode(thing, property, value) {
    const predicateNode = asNamedNode(property);
    const updatedThing = filterThing(thing, (quad) => {
        return (!quad.predicate.equals(predicateNode) ||
            !isNamedNode(quad.object) ||
            !quad.object.equals(value));
    });
    return updatedThing;
}
/**
 * @ignore This should not be needed due to the other remove*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing Thing to remove a Literal value from.
 * @param property Property for which to remove the given Literal value.
 * @param value Literal to remove from `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value removed for the given Property.
 */
function removeLiteral(thing, property, value) {
    const predicateNode = asNamedNode(property);
    const updatedThing = filterThing(thing, (quad) => {
        return (!quad.predicate.equals(predicateNode) ||
            !isLiteral(quad.object) ||
            !quad.object.equals(value));
    });
    return updatedThing;
}
/**
 * @param thing Thing to remove a Literal value from.
 * @param property Property for which to remove the given Literal value.
 * @param type Data type that the Literal should be stored as.
 * @param matcher Function that returns true if the given value is an equivalent serialisation of the value to remove. For example, when removing a `false` boolean, the matcher should return true for both "0" and "false".
 */
function removeLiteralMatching(thing, property, type, matcher) {
    const predicateNode = asNamedNode(property);
    const updatedThing = filterThing(thing, (quad) => {
        // Copy every value from the old thing into the new thing, unless it:
        return !(
        // has the predicate of the value-to-be-removed,
        (quad.predicate.equals(predicateNode) &&
            // also is a literal
            isLiteral(quad.object) &&
            // of the same type as the value-to-be-removed,
            quad.object.datatype.equals(DataFactory.namedNode(type)) &&
            // and has a value determined to be equal to the value-to-be-removed
            // by the given matcher (i.e. because their serialisations are equal):
            matcher(quad.object.value)));
    });
    return updatedThing;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Create a new Thing with existing values replaced by the given URL for the given Property.
 *
 * To preserve existing values, see [[addUrl]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set a URL value on.
 * @param property Property for which to set the given URL value.
 * @param url URL to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setUrl = (thing, property, url) => {
    const newThing = removeAll(thing, property);
    const predicateNode = asNamedNode(property);
    newThing.add(DataFactory.quad(internal_toNode(newThing), predicateNode, internal_toNode(url)));
    return newThing;
};
/** @hidden Alias of [[setUrl]] for those who prefer IRI terminology. */
const setIri = setUrl;
/**
 * Create a new Thing with existing values replaced by the given boolean for the given Property.
 *
 * To preserve existing values, see [[addBoolean]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set a boolean value on.
 * @param property Property for which to set the given boolean value.
 * @param value Boolean to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setBoolean = (thing, property, value) => {
    return setLiteralOfType(thing, property, serializeBoolean(value), xmlSchemaTypes.boolean);
};
/**
 * Create a new Thing with existing values replaced by the given datetime for the given Property.
 *
 * To preserve existing values, see [[addDatetime]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set an datetime value on.
 * @param property Property for which to set the given datetime value.
 * @param value Datetime to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setDatetime = (thing, property, value) => {
    return setLiteralOfType(thing, property, serializeDatetime(value), xmlSchemaTypes.dateTime);
};
/**
 * Create a new Thing with existing values replaced by the given decimal for the given Property.
 *
 * To preserve existing values, see [[addDecimal]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set a decimal value on.
 * @param property Property for which to set the given decimal value.
 * @param value Decimal to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setDecimal = (thing, property, value) => {
    return setLiteralOfType(thing, property, serializeDecimal(value), xmlSchemaTypes.decimal);
};
/**
 * Create a new Thing with existing values replaced by the given integer for the given Property.
 *
 * To preserve existing values, see [[addInteger]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set an integer value on.
 * @param property Property for which to set the given integer value.
 * @param value Integer to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setInteger = (thing, property, value) => {
    return setLiteralOfType(thing, property, serializeInteger(value), xmlSchemaTypes.integer);
};
/**
 * Create a new Thing with existing values replaced by the given localised string for the given Property.
 *
 * To preserve existing values, see [[addStringWithLocale]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set a localised string value on.
 * @param property Property for which to set the given localised string value.
 * @param value Localised string to set on `thing` for the given `property`.
 * @param locale Locale of the added string.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
function setStringWithLocale(thing, property, value, locale) {
    const literal = DataFactory.literal(value, normalizeLocale(locale));
    return setLiteral(thing, property, literal);
}
/**
 * Create a new Thing with existing values replaced by the given unlocalised string for the given Property.
 *
 * To preserve existing values, see [[addStringNoLocale]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to set an unlocalised string value on.
 * @param property Property for which to set the given unlocalised string value.
 * @param value Unlocalised string to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
const setStringNoLocale = (thing, property, value) => {
    return setLiteralOfType(thing, property, value, xmlSchemaTypes.string);
};
/**
 * Create a new Thing with existing values replaced by the given Named Node for the given Property.
 *
 * This replaces existing values for the given Property. To preserve them, see [[addNamedNode]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other set*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to set a NamedNode on.
 * @param property Property for which to set the value.
 * @param value The NamedNode to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
function setNamedNode(thing, property, value) {
    return setTerm(thing, property, value);
}
/**
 * Create a new Thing with existing values replaced by the given Literal for the given Property.
 *
 * This replaces existing values for the given Property. To preserve them, see [[addLiteral]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other set*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to set a Literal on.
 * @param property Property for which to set the value.
 * @param value The Literal to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 */
function setLiteral(thing, property, value) {
    return setTerm(thing, property, value);
}
/**
 * Creates a new Thing with existing values replaced by the given Term for the given Property.
 *
 * This replaces existing values for the given Property. To preserve them, see [[addTerm]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other set*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to set a Term on.
 * @param property Property for which to set the value.
 * @param value The raw RDF/JS value to set on `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with existing values replaced by the given value for the given Property.
 * @since 0.3.0
 */
function setTerm(thing, property, value) {
    const newThing = removeAll(thing, property);
    const predicateNode = asNamedNode(property);
    newThing.add(DataFactory.quad(internal_toNode(newThing), predicateNode, value));
    return newThing;
}
function setLiteralOfType(thing, property, value, type) {
    const literal = DataFactory.literal(value, type);
    return setLiteral(thing, property, literal);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/** @internal */
function internal_fetchResourceAcl(dataset, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasAccessibleAcl(dataset)) {
            return null;
        }
        try {
            const aclSolidDataset = yield getSolidDataset(dataset.internal_resourceInfo.aclUrl, options);
            return Object.assign(aclSolidDataset, {
                internal_accessTo: getSourceUrl(dataset),
            });
        }
        catch (e) {
            // Since a Solid server adds a `Link` header to an ACL even if that ACL does not exist,
            // failure to fetch the ACL is expected to happen - we just return `null` and let callers deal
            // with it.
            return null;
        }
    });
}
/** @internal */
function internal_fetchFallbackAcl(resource, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const resourceUrl = new URL(getSourceUrl(resource));
        const resourcePath = resourceUrl.pathname;
        // Note: we're currently assuming that the Origin is the root of the Pod. However, it is not yet
        //       set in stone that that will always be the case. We might need to check the Container's
        //       metadata at some point in time to check whether it is actually the root of the Pod.
        //       See: https://github.com/solid/specification/issues/153#issuecomment-624630022
        if (resourcePath === "/") {
            // We're already at the root, so there's no Container we can retrieve:
            return null;
        }
        const containerPath = internal_getContainerPath(resourcePath);
        const containerIri = new URL(containerPath, resourceUrl.origin).href;
        const containerInfo = yield getResourceInfo(containerIri, options);
        if (!hasAccessibleAcl(containerInfo)) {
            // If the current user does not have access to this Container's ACL,
            // we cannot determine whether its ACL is the one that applies. Thus, return null:
            return null;
        }
        const containerAcl = yield internal_fetchResourceAcl(containerInfo, options);
        if (containerAcl === null) {
            return internal_fetchFallbackAcl(containerInfo, options);
        }
        return containerAcl;
    });
}
/**
 * Given the path to a Resource, get the URL of the Container one level up in the hierarchy.
 * @param resourcePath The path of the Resource of which we need to determine the Container's path.
 * @hidden For internal use only.
 */
function internal_getContainerPath(resourcePath) {
    const resourcePathWithoutTrailingSlash = resourcePath.substring(resourcePath.length - 1) === "/"
        ? resourcePath.substring(0, resourcePath.length - 1)
        : resourcePath;
    const containerPath = resourcePath.substring(0, resourcePathWithoutTrailingSlash.lastIndexOf("/")) + "/";
    return containerPath;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Verifies whether the given Resource has a resource ACL (Access Control List) attached.
 *
 * The [[hasResourceAcl]] function checks that:
 * - a given Resource has a resource ACL attached, and
 * - the user calling [[hasResourceAcl]] has Control access to the Resource.
 *
 * To retrieve a Resource with its ACLs, see [[getSolidDatasetWithAcl]].
 *
 * @param resource A Resource that might have an ACL attached.
 * @returns `true` if the Resource has a resource ACL attached that is accessible by the user.
 */
function hasResourceAcl(resource) {
    return (resource.internal_acl.resourceAcl !== null &&
        getSourceUrl(resource) ===
            resource.internal_acl.resourceAcl.internal_accessTo &&
        resource.internal_resourceInfo.aclUrl ===
            getSourceUrl(resource.internal_acl.resourceAcl));
}
function getResourceAcl(resource) {
    if (!hasResourceAcl(resource)) {
        return null;
    }
    return resource.internal_acl.resourceAcl;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Verifies whether the given Resource has a fallback ACL (Access Control List) attached.
 *
 * A fallback ACL for a Resource is inherited from the Resource's parent Container
 * (or another of its ancestor Containers) and applies if the Resource does
 * not have its own resource ACL.
 *
 * The [[hasFallbackAcl]] function checks that:
 * - a given Resource has a fallback ACL attached, and
 * - the user calling [[hasFallbackAcl]] has Control access to the Container
 * from which the Resource inherits its ACL.
 *
 * To retrieve a Resource with its ACLs, see [[getSolidDatasetWithAcl]].
 *
 * @param resource A [[SolidDataset]] that might have a fallback ACL attached.
 *
 * @returns `true` if the Resource has a fallback ACL attached that is accessible to the user.
 */
function hasFallbackAcl(resource) {
    return resource.internal_acl.fallbackAcl !== null;
}
function getFallbackAcl(dataset) {
    if (!hasFallbackAcl(dataset)) {
        return null;
    }
    return dataset.internal_acl.fallbackAcl;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Creates an empty resource ACL (Access Control List) for a given Resource.
 *
 * @param targetResource A Resource that does not have its own ACL yet (see [[hasResourceAcl]]).
 * @returns An empty resource ACL for the given Resource.
 */
function createAcl(targetResource) {
    const emptyResourceAcl = Object.assign(dataset(), {
        internal_accessTo: getSourceUrl(targetResource),
        internal_resourceInfo: {
            sourceIri: targetResource.internal_resourceInfo.aclUrl,
            isRawData: false,
            linkedResources: {},
        },
    });
    return emptyResourceAcl;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Creates a resource ACL (Access Control List), initialised from the fallback ACL
 * inherited from the given Resource's Container (or another of its ancestor Containers).
 * That is, the new ACL has the same rules/entries as the fallback ACL that currently
 * applies to the Resource.
 *
 * @param resource A Resource without its own resource ACL (see [[hasResourceAcl]]) but with an accessible fallback ACL (see [[hasFallbackAcl]]).
 * @returns A resource ACL initialised with the rules/entries from the Resource's fallback ACL.
 */
function createAclFromFallbackAcl(resource) {
    const emptyResourceAcl = createAcl(resource);
    const fallbackAclRules = internal_getAclRules(resource.internal_acl.fallbackAcl);
    const defaultAclRules = internal_getDefaultAclRulesForResource(fallbackAclRules, resource.internal_acl.fallbackAcl.internal_accessTo);
    const newAclRules = defaultAclRules.map((rule) => {
        rule = removeAll(rule, acl.default);
        rule = removeAll(rule, acl.defaultForNew);
        rule = setIri(rule, acl.accessTo, getSourceUrl(resource));
        rule = setIri(rule, acl.default, getSourceUrl(resource));
        return rule;
    });
    // Iterate over every ACL Rule we want to import, inserting them into `emptyResourceAcl` one by one:
    const initialisedResourceAcl = newAclRules.reduce(setThing, emptyResourceAcl);
    return initialisedResourceAcl;
}
/** @internal */
function internal_getAclRules(aclDataset) {
    const things = getThingAll(aclDataset);
    return things.filter(isAclRule);
}
function isAclRule(thing) {
    return getIriAll(thing, rdf.type).includes(acl.Authorization);
}
/** @internal */
function internal_getResourceAclRulesForResource(aclRules, resource) {
    return aclRules.filter((rule) => appliesToResource(rule, resource));
}
function appliesToResource(aclRule, resource) {
    return getIriAll(aclRule, acl.accessTo).includes(resource);
}
/** @internal */
function internal_getDefaultAclRulesForResource(aclRules, resource) {
    return aclRules.filter((rule) => isDefaultForResource(rule, resource));
}
function isDefaultForResource(aclRule, resource) {
    return (getIriAll(aclRule, acl.default).includes(resource) ||
        getIriAll(aclRule, acl.defaultForNew).includes(resource));
}
/** @internal */
function internal_getAccess(rule) {
    const ruleAccessModes = getIriAll(rule, acl.mode);
    const writeAccess = ruleAccessModes.includes(internal_accessModeIriStrings.write);
    return writeAccess
        ? {
            read: ruleAccessModes.includes(internal_accessModeIriStrings.read),
            append: true,
            write: true,
            control: ruleAccessModes.includes(internal_accessModeIriStrings.control),
        }
        : {
            read: ruleAccessModes.includes(internal_accessModeIriStrings.read),
            append: ruleAccessModes.includes(internal_accessModeIriStrings.append),
            write: false,
            control: ruleAccessModes.includes(internal_accessModeIriStrings.control),
        };
}
/** @internal */
function internal_combineAccessModes(modes) {
    return modes.reduce((accumulator, current) => {
        const writeAccess = accumulator.write || current.write;
        return writeAccess
            ? {
                read: accumulator.read || current.read,
                append: true,
                write: true,
                control: accumulator.control || current.control,
            }
            : {
                read: accumulator.read || current.read,
                append: accumulator.append || current.append,
                write: false,
                control: accumulator.control || current.control,
            };
    }, { read: false, append: false, write: false, control: false });
}
/** @internal */
function internal_removeEmptyAclRules(aclDataset) {
    const aclRules = internal_getAclRules(aclDataset);
    const aclRulesToRemove = aclRules.filter(isEmptyAclRule);
    // Is this too clever? It iterates over aclRulesToRemove, one by one removing them from aclDataset.
    const updatedAclDataset = aclRulesToRemove.reduce(removeThing, aclDataset);
    return updatedAclDataset;
}
function isEmptyAclRule(aclRule) {
    // If there are Quads in there unrelated to Access Control,
    // this is not an empty ACL rule that can be deleted:
    if (Array.from(aclRule).some((quad) => !isAclQuad(quad))) {
        return false;
    }
    // If the rule does not apply to any Resource, it is no longer working:
    if (getIri(aclRule, acl.accessTo) === null &&
        getIri(aclRule, acl.default) === null &&
        getIri(aclRule, acl.defaultForNew) === null) {
        return true;
    }
    // If the rule does not specify Access Modes, it is no longer working:
    if (getIri(aclRule, acl.mode) === null) {
        return true;
    }
    // If the rule does not specify whom it applies to, it is no longer working:
    if (getIri(aclRule, acl.agent) === null &&
        getIri(aclRule, acl.agentGroup) === null &&
        getIri(aclRule, acl.agentClass) === null) {
        return true;
    }
    return false;
}
function isAclQuad(quad) {
    const predicate = quad.predicate;
    const object = quad.object;
    if (predicate.equals(DataFactory.namedNode(rdf.type)) &&
        object.equals(DataFactory.namedNode(acl.Authorization))) {
        return true;
    }
    if (predicate.equals(DataFactory.namedNode(acl.accessTo)) ||
        predicate.equals(DataFactory.namedNode(acl.default)) ||
        predicate.equals(DataFactory.namedNode(acl.defaultForNew))) {
        return true;
    }
    if (predicate.equals(DataFactory.namedNode(acl.mode)) &&
        Object.values(internal_accessModeIriStrings).some((mode) => object.equals(DataFactory.namedNode(mode)))) {
        return true;
    }
    if (predicate.equals(DataFactory.namedNode(acl.agent)) ||
        predicate.equals(DataFactory.namedNode(acl.agentGroup)) ||
        predicate.equals(DataFactory.namedNode(acl.agentClass))) {
        return true;
    }
    if (predicate.equals(DataFactory.namedNode(acl.origin))) {
        return true;
    }
    return false;
}
/**
 * IRIs of potential Access Modes
 * @internal
 */
const internal_accessModeIriStrings = {
    read: "http://www.w3.org/ns/auth/acl#Read",
    append: "http://www.w3.org/ns/auth/acl#Append",
    write: "http://www.w3.org/ns/auth/acl#Write",
    control: "http://www.w3.org/ns/auth/acl#Control",
};
/** @internal
 * This function finds, among a set of ACL rules, the ones granting access to a given entity (the target)
 * and identifying it with a specific property (`acl:agent` or `acl:agentGroup`).
 * @param aclRules The set of rules to filter
 * @param targetIri The IRI of the target
 * @param targetType The property linking the rule to the target
 */
function internal_getAclRulesForIri(aclRules, targetIri, targetType) {
    return aclRules.filter((rule) => getIriAll(rule, targetType).includes(targetIri));
}
/** @internal
 * This function transforms a given set of rules into a map associating the IRIs
 * of the entities to which permissions are granted by these rules, and the permissions
 * granted to them. Additionally, it filters these entities based on the predicate
 * that refers to them in the rule.
 */
function internal_getAccessByIri(aclRules, targetType) {
    const targetIriAccess = {};
    aclRules.forEach((rule) => {
        const ruleTargetIri = getIriAll(rule, targetType);
        const access = internal_getAccess(rule);
        // A rule might apply to multiple agents. If multiple rules apply to the same agent, the Access
        // Modes granted by those rules should be combined:
        ruleTargetIri.forEach((targetIri) => {
            targetIriAccess[targetIri] =
                typeof targetIriAccess[targetIri] === "undefined"
                    ? access
                    : internal_combineAccessModes([targetIriAccess[targetIri], access]);
        });
    });
    return targetIriAccess;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Saves the resource ACL for a Resource.
 *
 * @param resource The Resource to which the given resource ACL applies.
 * @param resourceAcl An [[AclDataset]] whose ACL Rules will apply to `resource`.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 */
function saveAclFor(resource, resourceAcl, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasAccessibleAcl(resource)) {
            throw new Error(`Could not determine the location of the ACL for the Resource at \`${getSourceUrl(resource)}\`; possibly the current user does not have Control access to that Resource. Try calling \`hasAccessibleAcl()\` before calling \`saveAclFor()\`.`);
        }
        const savedDataset = yield saveSolidDatasetAt(resource.internal_resourceInfo.aclUrl, resourceAcl, options);
        const savedAclDataset = Object.assign(savedDataset, {
            internal_accessTo: getSourceUrl(resource),
        });
        return savedAclDataset;
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Removes the resource ACL (Access Control List) from a Resource.
 *
 * Once the resource ACL is removed from the Resource, the Resource relies on the
 * fallback ACL inherited from the Resource's parent Container (or another of its ancestor Containers).
 *
 * @param resource The Resource for which you want to delete the ACL.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 */
function deleteAclFor(resource, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const response = yield config.fetch(resource.internal_resourceInfo.aclUrl, {
            method: "DELETE",
        });
        if (!response.ok) {
            throw new Error(`Deleting the ACL of the Resource at \`${getSourceUrl(resource)}\` failed: \`${response.status}\` \`${response.statusText}\`.`);
        }
        const storedResource = Object.assign(internal_cloneResource(resource), {
            acl: {
                resourceAcl: null,
            },
        });
        return storedResource;
    });
}
/**
 * Initialises a new ACL Rule that grants some access - but does not yet specify to whom.
 *
 * @hidden This is an internal utility function that should not be used directly by downstreams.
 * @param access Access mode that this Rule will grant
 */
function internal_initialiseAclRule(access) {
    let newRule = createThing();
    newRule = setIri(newRule, rdf.type, acl.Authorization);
    if (access.read) {
        newRule = addIri(newRule, acl.mode, internal_accessModeIriStrings.read);
    }
    if (access.append && !access.write) {
        newRule = addIri(newRule, acl.mode, internal_accessModeIriStrings.append);
    }
    if (access.write) {
        newRule = addIri(newRule, acl.mode, internal_accessModeIriStrings.write);
    }
    if (access.control) {
        newRule = addIri(newRule, acl.mode, internal_accessModeIriStrings.control);
    }
    return newRule;
}
/**
 * Creates a new ACL Rule with the same ACL values as the input ACL Rule, but having a different IRI.
 *
 * Note that non-ACL values will not be copied over.
 *
 * @hidden This is an internal utility function that should not be used directly by downstreams.
 * @param sourceRule ACL rule to duplicate.
 */
function internal_duplicateAclRule(sourceRule) {
    let targetRule = createThing();
    targetRule = setIri(targetRule, rdf.type, acl.Authorization);
    function copyIris(inputRule, outputRule, predicate) {
        return getIriAll(inputRule, predicate).reduce((outputRule, iriTarget) => addIri(outputRule, predicate, iriTarget), outputRule);
    }
    targetRule = copyIris(sourceRule, targetRule, acl.accessTo);
    targetRule = copyIris(sourceRule, targetRule, acl.default);
    targetRule = copyIris(sourceRule, targetRule, acl.defaultForNew);
    targetRule = copyIris(sourceRule, targetRule, acl.agent);
    targetRule = copyIris(sourceRule, targetRule, acl.agentGroup);
    targetRule = copyIris(sourceRule, targetRule, acl.agentClass);
    targetRule = copyIris(sourceRule, targetRule, acl.origin);
    targetRule = copyIris(sourceRule, targetRule, acl.mode);
    return targetRule;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/** @ignore For internal use only. */
const internal_defaultFetchOptions = {
    fetch: fetch,
};
/**
 * Retrieve the information about a resource (e.g. access permissions) without
 * fetching the resource itself.
 *
 * @param url URL to fetch Resource metadata from.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch#Parameters).
 * @returns Promise resolving to the metadata describing the given Resource, or rejecting if fetching it failed.
 * @since 0.4.0
 */
function getResourceInfo(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const response = yield config.fetch(url, { method: "HEAD" });
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Fetching the metadata of the Resource at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const resourceInfo = internal_parseResourceInfo(response);
        return { internal_resourceInfo: resourceInfo };
    });
}
/**
 * This (currently internal) function fetches the ACL indicated in the [[WithServerResourceInfo]]
 * attached to a resource.
 *
 * @internal
 * @param resourceInfo The Resource info with the ACL URL
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch#Parameters).
 */
function internal_fetchAcl(resourceInfo, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasAccessibleAcl(resourceInfo)) {
            return {
                resourceAcl: null,
                fallbackAcl: null,
            };
        }
        const resourceAcl = yield internal_fetchResourceAcl(resourceInfo, options);
        const acl = resourceAcl === null
            ? {
                resourceAcl: null,
                fallbackAcl: yield internal_fetchFallbackAcl(resourceInfo, options),
            }
            : { resourceAcl: resourceAcl, fallbackAcl: null };
        return acl;
    });
}
/**
 * Experimental: fetch a Resource's metadata and its associated Access Control List.
 *
 * This is an experimental function that fetches both a Resource's metadata, the linked ACL Resource (if
 * available), and the ACL that applies to it if the linked ACL Resource is not available (if accessible). This can
 * result in many HTTP requests being executed, in lieu of the Solid spec mandating servers to
 * provide this info in a single request.
 *
 * If the Resource's linked ACL Resource could not be fetched (because it does not exist, or because
 * the authenticated user does not have access to it), `acl.resourceAcl` will be `null`. If the
 * applicable Container's ACL is not accessible to the authenticated user, `acl.fallbackAcl` will be
 * `null`.
 *
 * @param url URL of the SolidDataset to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A Resource's metadata and the ACLs that apply to the Resource, if available to the authenticated user.
 */
function getResourceInfoWithAcl(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const resourceInfo = yield getResourceInfo(url, options);
        const acl = yield internal_fetchAcl(resourceInfo, options);
        return Object.assign(resourceInfo, { internal_acl: acl });
    });
}
/**
 * @internal
 */
function internal_parseResourceInfo(response) {
    var _a, _b, _c;
    const contentTypeParts = (_b = (_a = response.headers.get("Content-Type")) === null || _a === void 0 ? void 0 : _a.split(";")) !== null && _b !== void 0 ? _b : [];
    // If the server offers a Turtle or JSON-LD serialisation on its own accord,
    // that tells us whether it is RDF data that the server can understand
    // (and hence can be updated with a PATCH request with SPARQL INSERT and DELETE statements),
    // in which case our SolidDataset-related functions should handle it.
    // For more context, see https://github.com/inrupt/solid-client-js/pull/214.
    const isSolidDataset = contentTypeParts.length > 0 &&
        ["text/turtle", "application/ld+json"].includes(contentTypeParts[0]);
    const resourceInfo = {
        sourceIri: response.url,
        isRawData: !isSolidDataset,
        contentType: (_c = response.headers.get("Content-Type")) !== null && _c !== void 0 ? _c : undefined,
        linkedResources: {},
    };
    const linkHeader = response.headers.get("Link");
    if (linkHeader) {
        const parsedLinks = LinkHeader.parse(linkHeader);
        // Set ACL link
        const aclLinks = parsedLinks.get("rel", "acl");
        if (aclLinks.length === 1) {
            resourceInfo.aclUrl = new URL(aclLinks[0].uri, resourceInfo.sourceIri).href;
        }
        // Parse all link headers and expose them in a standard way
        // (this can replace the parsing of the ACL link above):
        resourceInfo.linkedResources = parsedLinks.refs.reduce((rels, ref) => {
            var _a;
            var _b;
            (_a = rels[_b = ref.rel]) !== null && _a !== void 0 ? _a : (rels[_b] = []);
            rels[ref.rel].push(new URL(ref.uri, resourceInfo.sourceIri).href);
            return rels;
        }, resourceInfo.linkedResources);
    }
    const wacAllowHeader = response.headers.get("WAC-Allow");
    if (wacAllowHeader) {
        resourceInfo.permissions = parseWacAllowHeader(wacAllowHeader);
    }
    return resourceInfo;
}
/**
 * @param resource Resource for which to check whether it is a Container.
 * @returns Whether `resource` is a Container.
 */
function isContainer(resource) {
    const containerUrl = hasResourceInfo(resource)
        ? getSourceUrl(resource)
        : internal_toIriString(resource);
    return containerUrl.endsWith("/");
}
/**
 * This function will tell you whether a given Resource contains raw data, or a SolidDataset.
 *
 * @param resource Resource for which to check whether it contains raw data.
 * @return Whether `resource` contains raw data.
 */
function isRawData(resource) {
    return resource.internal_resourceInfo.isRawData;
}
/**
 * @param resource Resource for which to determine the Content Type.
 * @returns The Content Type, if known, or null if not known.
 */
function getContentType(resource) {
    var _a;
    return (_a = resource.internal_resourceInfo.contentType) !== null && _a !== void 0 ? _a : null;
}
function getSourceUrl(resource) {
    if (hasResourceInfo(resource)) {
        return resource.internal_resourceInfo.sourceIri;
    }
    return null;
}
/** @hidden Alias of getSourceUrl for those who prefer to use IRI terminology. */
const getSourceIri = getSourceUrl;
/** @hidden Used to instantiate a separate instance from input parameters */
function internal_cloneResource(resource) {
    let clonedResource;
    if (typeof resource.slice === "function") {
        // If given Resource is a File:
        clonedResource = resource.slice();
    }
    else if (typeof resource.match === "function") {
        // If given Resource is a SolidDataset:
        // (We use the existince of a `match` method as a heuristic:)
        clonedResource = clone(resource);
    }
    else {
        // If it is just a plain object containing metadata:
        clonedResource = Object.assign({}, resource);
    }
    return Object.assign(clonedResource, 
    // Although the RDF/JS data structures use classes and mutation,
    // we only attach atomic properties that we never mutate.
    // Hence, `copyNonClassProperties` is a heuristic that allows us to only clone our own data
    // structures, rather than references to the same mutable instances of RDF/JS data structures:
    copyNonClassProperties(resource));
}
function copyNonClassProperties(source) {
    const copy = {};
    Object.keys(source).forEach((key) => {
        const value = source[key];
        if (typeof value !== "object" || value === null) {
            copy[key] = value;
            return;
        }
        if (value.constructor.name !== "Object") {
            return;
        }
        copy[key] = value;
    });
    return copy;
}
/**
 * Given a Resource that exposes information about the owner of the Pod it is in, returns the WebID of that owner.
 *
 * Data about the owner of the Pod is exposed when the following conditions hold:
 * - The Pod server supports exposing the Pod owner
 * - The current user is allowed to see who the Pod owner is.
 *
 * If one or more of those conditions are false, this function will return `null`.
 *
 * @param resource A Resource that contains information about the owner of the Pod it is in.
 * @returns The WebID of the owner of the Pod the Resource is in, if provided, or `null` if not.
 * @since 0.6.0
 */
function getPodOwner(resource) {
    var _a;
    if (!hasServerResourceInfo(resource)) {
        return null;
    }
    const podOwners = (_a = resource.internal_resourceInfo.linkedResources["http://www.w3.org/ns/solid/terms#podOwner"]) !== null && _a !== void 0 ? _a : [];
    return podOwners.length === 1 ? podOwners[0] : null;
}
/**
 * Given a WebID and a Resource that exposes information about the owner of the Pod it is in, returns whether the given WebID is the owner of the Pod.
 *
 * Data about the owner of the Pod is exposed when the following conditions hold:
 * - The Pod server supports exposing the Pod owner
 * - The current user is allowed to see who the Pod owner is.
 *
 * If one or more of those conditions are false, this function will return `null`.
 *
 * @param webId The WebID of which to check whether it is the Pod Owner's.
 * @param resource A Resource that contains information about the owner of the Pod it is in.
 * @returns Whether the given WebID is the Pod Owner's, if the Pod Owner is exposed, or `null` if it is not exposed.
 * @since 0.6.0
 */
function isPodOwner(webId, resource) {
    const podOwner = getPodOwner(resource);
    if (typeof podOwner !== "string") {
        return null;
    }
    return podOwner === webId;
}
/**
 * Extends the regular JavaScript error object with access to the status code and status message.
 */
class FetchError extends Error {
    constructor(message, errorResponse) {
        super(message);
        this.statusCode = errorResponse.status;
        this.statusText = errorResponse.statusText;
    }
}
/** @internal */
function internal_isUnsuccessfulResponse(response) {
    return !response.ok;
}
/**
 * Parse a WAC-Allow header into user and public access booleans.
 *
 * @param wacAllowHeader A WAC-Allow header in the format `user="read append write control",public="read"`
 * @see https://github.com/solid/solid-spec/blob/cb1373a369398d561b909009bd0e5a8c3fec953b/api-rest.md#wac-allow-headers
 */
function parseWacAllowHeader(wacAllowHeader) {
    function parsePermissionStatement(permissionStatement) {
        const permissions = permissionStatement.split(" ");
        const writePermission = permissions.includes("write");
        return writePermission
            ? {
                read: permissions.includes("read"),
                append: true,
                write: true,
                control: permissions.includes("control"),
            }
            : {
                read: permissions.includes("read"),
                append: permissions.includes("append"),
                write: false,
                control: permissions.includes("control"),
            };
    }
    function getStatementFor(header, scope) {
        const relevantEntries = header
            .split(",")
            .map((rawEntry) => rawEntry.split("="))
            .filter((parts) => parts.length === 2 && parts[0].trim() === scope);
        // There should only be one statement with the given scope:
        if (relevantEntries.length !== 1) {
            return "";
        }
        const relevantStatement = relevantEntries[0][1].trim();
        // The given statement should be wrapped in double quotes to be valid:
        if (relevantStatement.charAt(0) !== '"' ||
            relevantStatement.charAt(relevantStatement.length - 1) !== '"') {
            return "";
        }
        // Return the statment without the wrapping quotes, e.g.: read append write control
        return relevantStatement.substring(1, relevantStatement.length - 1);
    }
    return {
        user: parsePermissionStatement(getStatementFor(wacAllowHeader, "user")),
        public: parsePermissionStatement(getStatementFor(wacAllowHeader, "public")),
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const defaultGetFileOptions = {
    fetch: fetch,
};
const RESERVED_HEADERS = ["Slug", "If-None-Match", "Content-Type"];
/**
 * Some of the headers must be set by the library, rather than directly.
 */
function containsReserved(header) {
    return RESERVED_HEADERS.some((reserved) => header[reserved] !== undefined);
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Retrieves a file from a URL and returns the file as a blob.
 *
 * @param url The URL of the file to return
 * @param options Fetching options: a custom fetcher and/or headers.
 * @returns The file as a blob.
 */
function getFile(input, options = defaultGetFileOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, defaultGetFileOptions), options);
        const url = internal_toIriString(input);
        const response = yield config.fetch(url, config.init);
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Fetching the File failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const resourceInfo = internal_parseResourceInfo(response);
        const data = yield response.blob();
        const fileWithResourceInfo = Object.assign(data, {
            internal_resourceInfo: resourceInfo,
        });
        return fileWithResourceInfo;
    });
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Retrieves a file, its resource ACL (Access Control List) if available,
 * and its fallback ACL from a URL and returns them as a blob.
 *
 * If the user calling the function does not have access to the file's resource ACL,
 * [[hasAccessibleAcl]] on the returned blob returns false.
 * If the user has access to the file's resource ACL but the resource ACL does not exist,
 * [[getResourceAcl]] on the returned blob returns null.
 * If the fallback ACL is inaccessible by the user,
 * [[getFallbackAcl]] on the returned blob returns null.
 *
 * ```{tip}
 * To retrieve the ACLs, the function results in multiple HTTP requests rather than a single
 * request as mandated by the Solid spec. As such, prefer [[getFile]] instead if you do not need the ACL.
 * ```
 *
 * @param url The URL of the fetched file
 * @param options Fetching options: a custom fetcher and/or headers.
 * @returns A file and its ACLs, if available to the authenticated user, as a blob.
 * @since 0.2.0
 */
function getFileWithAcl(input, options = defaultGetFileOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const file = yield getFile(input, options);
        const acl = yield internal_fetchAcl(file, options);
        return Object.assign(file, { internal_acl: acl });
    });
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 * Deletes a file at a given URL.
 *
 * @param file The URL of the file to delete
 */
function deleteFile(file, options = defaultGetFileOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, defaultGetFileOptions), options);
        const url = hasResourceInfo(file)
            ? internal_toIriString(getSourceIri(file))
            : internal_toIriString(file);
        const response = yield config.fetch(url, Object.assign(Object.assign({}, config.init), { method: "DELETE" }));
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Deleting the file at \`${url}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
    });
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Saves a file in a folder associated with the given URL. The final filename may or may
 * not be the given `slug`.
 *
 * @param folderUrl The URL of the folder where the new file is saved.
 * @param file The file to be written.
 * @param options Additional parameters for file creation (e.g. a slug).
 * @returns A Promise that resolves to the saved file, if available, or `null` if the current user does not have Read access to the newly-saved file. It rejects if saving fails.
 */
function saveFileInContainer(folderUrl, file, options = defaultGetFileOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const folderUrlString = internal_toIriString(folderUrl);
        const response = yield writeFile(folderUrlString, file, "POST", options);
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Saving the file in \`${folderUrl}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const locationHeader = response.headers.get("Location");
        if (locationHeader === null) {
            throw new Error("Could not determine the location of the newly saved file.");
        }
        const fileIri = new URL(locationHeader, new URL(folderUrlString).origin).href;
        const blobClone = internal_cloneResource(file);
        const resourceInfo = {
            internal_resourceInfo: {
                isRawData: true,
                sourceIri: fileIri,
                contentType: file.type.length > 0 ? file.type : undefined,
            },
        };
        return Object.assign(blobClone, resourceInfo);
    });
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Saves a file at a given URL, replacing any previous content.
 *
 * @param fileUrl The URL where the file is saved.
 * @param file The file to be written.
 * @param options Additional parameters for file creation (e.g. a slug).
 */
function overwriteFile(fileUrl, file, options = defaultGetFileOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const fileUrlString = internal_toIriString(fileUrl);
        const response = yield writeFile(fileUrlString, file, "PUT", options);
        if (internal_isUnsuccessfulResponse(response)) {
            throw new FetchError(`Overwriting the file at \`${fileUrlString}\` failed: \`${response.status}\` \`${response.statusText}\`.`, response);
        }
        const blobClone = internal_cloneResource(file);
        const resourceInfo = internal_parseResourceInfo(response);
        resourceInfo.sourceIri = fileUrlString;
        resourceInfo.isRawData = true;
        return Object.assign(blobClone, { internal_resourceInfo: resourceInfo });
    });
}
function isHeadersArray(headers) {
    return Array.isArray(headers);
}
/**
 * The return type of this function is misleading: it should ONLY be used to check
 * whether an object has a forEach method that returns <key, value> pairs.
 *
 * @param headers A headers object that might have a forEach
 */
function hasHeadersObjectForEach(headers) {
    return typeof headers.forEach === "function";
}
/**
 * @hidden
 * This function feels unnecessarily complicated, but is required in order to
 * have Headers according to type definitions in both Node and browser environments.
 * This might require a fix upstream to be cleaned up.
 *
 * @param headersToFlatten A structure containing headers potentially in several formats
 */
function flattenHeaders(headersToFlatten) {
    if (typeof headersToFlatten === "undefined") {
        return {};
    }
    let flatHeaders = {};
    if (isHeadersArray(headersToFlatten)) {
        headersToFlatten.forEach(([key, value]) => {
            flatHeaders[key] = value;
        });
        // Note that the following line must be a elsif, because string[][] has a forEach,
        // but it returns string[] instead of <key, value>
    }
    else if (hasHeadersObjectForEach(headersToFlatten)) {
        headersToFlatten.forEach((value, key) => {
            flatHeaders[key] = value;
        });
    }
    else {
        // If the headers are already a Record<string, string>,
        // they can directly be returned.
        flatHeaders = headersToFlatten;
    }
    return flatHeaders;
}
/**
 * Internal function that performs the actual write HTTP query, either POST
 * or PUT depending on the use case.
 *
 * @param fileUrl The URL where the file is saved
 * @param file The file to be written
 * @param method The HTTP method
 * @param options Additional parameters for file creation (e.g. a slug)
 */
function writeFile(targetUrl, file, method, options) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const config = Object.assign(Object.assign({}, defaultGetFileOptions), options);
        const headers = flattenHeaders((_b = (_a = config.init) === null || _a === void 0 ? void 0 : _a.headers) !== null && _b !== void 0 ? _b : {});
        if (containsReserved(headers)) {
            throw new Error(`No reserved header (${RESERVED_HEADERS.join(", ")}) should be set in the optional RequestInit.`);
        }
        // If a slug is in the parameters, set the request headers accordingly
        if (config.slug !== undefined) {
            headers["Slug"] = config.slug;
        }
        headers["Content-Type"] = file.type;
        const targetUrlString = internal_toIriString(targetUrl);
        return yield config.fetch(targetUrlString, Object.assign(Object.assign({}, config.init), { headers,
            method, body: file }));
    });
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{warning}
 * Do not use this function in production code. For use in **unit tests**.
 * ```
 *
 * This function initialises a new [[SolidDataset]] with metadata as though the
 * SolidDataset has been retrieved from the given URL. The mock SolidDataset can be used in
 * unit tests that require persisted SolidDatasets; e.g., unit tests that call [[getSourceUrl]].
 *
 * @param url The URL from which the returned SolidDataset appears to be retrieved.
 * @returns A mock SolidDataset that appears to be retrieved from the `url`.
 * @since 0.2.0
 */
function mockSolidDatasetFrom(url) {
    const solidDataset = createSolidDataset();
    const solidDatasetWithResourceInfo = Object.assign(solidDataset, {
        internal_resourceInfo: {
            sourceIri: internal_toIriString(url),
            isRawData: false,
            contentType: "text/turtle",
            linkedResources: {},
        },
    });
    return solidDatasetWithResourceInfo;
}
/**
 * ```{warning}
 * Do not use this function in production code. For use in **unit tests**.
 * ```
 *
 * This function initialises a new Container [[SolidDataset]] with metadata as though the
 * Container has been retrieved from the given URL. The mock SolidDataset can be used in
 * unit tests that require persisted Containers; e.g., unit tests that call [[isContainer]].
 *
 * @param url The URL from which the returned Container appears to be retrieved. The `url` must end in a slash.
 * @returns A mock SolidDataset that appears to be retrieved from the `url`.
 * @since 0.2.0
 */
function mockContainerFrom(url) {
    const sourceIri = internal_toIriString(url);
    if (!sourceIri.endsWith("/")) {
        throw new Error("A Container's URL should end in a slash. Please update your tests.");
    }
    return mockSolidDatasetFrom(sourceIri);
}
/**
 * ```{warning}
 * Do not use this function in production code. For use in **unit tests**.
 * ```
 *
 * This function initialises a new File with metadata as though the
 * File has been retrieved from the given URL. The mock File can be used in
 * unit tests that require persisted Files; e.g. unit tests that call [[getSourceUrl]].
 *
 * @param url The URL from which the returned File appears to be retrieved.
 * @returns A mock File that appears to be retrieved from the `url`.
 * @since 0.2.0
 */
function mockFileFrom(url, options) {
    const file = new Blob();
    const fileWithResourceInfo = Object.assign(file, {
        internal_resourceInfo: {
            sourceIri: internal_toIriString(url),
            isRawData: true,
            contentType: options === null || options === void 0 ? void 0 : options.contentType,
            linkedResources: {},
        },
    });
    return fileWithResourceInfo;
}
/**
 * ```{warning}
 * Do not use this function in production code. For use in **unit tests**.
 * ```
 *
 * This function initialises a new Error object with metadata as though the
 * it was the result of getting a 404 when trying to fetch the Resource at the
 * given URL. The mock Error can be used in unit tests that require functions
 * that fetch Resources (like [[getSolidDataset]]) to fail.
 *
 * @param url The URL of the Resource that could not be fetched according to the error.
 * @param statusCode Optional status code (defaults to 404) that caused the error.
 * @returns A mock Error that represents not having been able to fetch the Resource at `url` due to a 404 Response.
 * @since 1.1.0
 */
function mockFetchError(fetchedUrl, statusCode = 404) {
    const failedResponse = new Response(undefined, {
        status: statusCode,
    });
    return new FetchError(`Fetching the Resource at \`${fetchedUrl}\` failed: \`${failedResponse.status}\` \`${failedResponse.statusText}\`.`, failedResponse);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Create a new Thing with a URL added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setUrl]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add a URL value to.
 * @param property Property for which to add the given URL value.
 * @param url URL to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addUrl = (thing, property, url) => {
    const predicateNode = asNamedNode(property);
    const newThing = cloneThing(thing);
    newThing.add(DataFactory.quad(internal_toNode(newThing), predicateNode, internal_toNode(url)));
    return newThing;
};
/** @hidden Alias for [[addUrl]] for those who prefer IRI terminology. */
const addIri = addUrl;
/**
 * Create a new Thing with a boolean added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setBoolean]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add a boolean value to.
 * @param property Property for which to add the given boolean value.
 * @param value Boolean to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addBoolean = (thing, property, value) => {
    return addLiteralOfType(thing, property, serializeBoolean(value), xmlSchemaTypes.boolean);
};
/**
 * Create a new Thing with a datetime added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setDatetime]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add a datetime value to.
 * @param property Property for which to add the given datetime value.
 * @param value Datetime to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addDatetime = (thing, property, value) => {
    return addLiteralOfType(thing, property, serializeDatetime(value), xmlSchemaTypes.dateTime);
};
/**
 * Create a new Thing with a decimal added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setDecimal]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add a decimal value to.
 * @param property Property for which to add the given decimal value.
 * @param value Decimal to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addDecimal = (thing, property, value) => {
    return addLiteralOfType(thing, property, serializeDecimal(value), xmlSchemaTypes.decimal);
};
/**
 * Create a new Thing with an integer added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setInteger]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add an integer value to.
 * @param property Property for which to add the given integer value.
 * @param value Integer to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addInteger = (thing, property, value) => {
    return addLiteralOfType(thing, property, serializeInteger(value), xmlSchemaTypes.integer);
};
/**
 * Create a new Thing with a localised string added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setStringWithLocale]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add a localised string value to.
 * @param property Property for which to add the given string value.
 * @param value String to add to `thing` for the given `property`.
 * @param locale Locale of the added string.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
function addStringWithLocale(thing, property, value, locale) {
    const literal = DataFactory.literal(value, normalizeLocale(locale));
    return addLiteral(thing, property, literal);
}
/**
 * Create a new Thing with an unlocalised string added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setStringNoLocale]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @param thing Thing to add an unlocalised string value to.
 * @param property Property for which to add the given string value.
 * @param value String to add to `thing` for the given `property`.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
const addStringNoLocale = (thing, property, value) => {
    return addLiteralOfType(thing, property, value, xmlSchemaTypes.string);
};
/**
 * Create a new Thing with a Named Node added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setNamedNode]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other add*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to add a Named Node to.
 * @param property Property for which to add a value.
 * @param value The Named Node to add.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
function addNamedNode(thing, property, value) {
    return addTerm(thing, property, value);
}
/**
 * Create a new Thing with a Literal added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setLiteral]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other add*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to add a Literal to.
 * @param property Property for which to add a value.
 * @param value The Literal to add.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 */
function addLiteral(thing, property, value) {
    return addTerm(thing, property, value);
}
/**
 * Creates a new Thing with a Term added for a Property.
 *
 * This preserves existing values for the given Property. To replace them, see [[setTerm]].
 *
 * The original `thing` is not modified; this function returns a cloned Thing with updated values.
 *
 * @ignore This should not be needed due to the other add*() functions. If you do find yourself needing it, please file a feature request for your use case.
 * @param thing The [[Thing]] to add a Term to.
 * @param property Property for which to add a value.
 * @param value The Term to add.
 * @returns A new Thing equal to the input Thing with the given value added for the given Property.
 * @since 0.3.0
 */
function addTerm(thing, property, value) {
    const predicateNode = asNamedNode(property);
    const newThing = cloneThing(thing);
    newThing.add(DataFactory.quad(internal_toNode(newThing), predicateNode, value));
    return newThing;
}
function addLiteralOfType(thing, property, value, type) {
    const literal = DataFactory.literal(value, type);
    return addLiteral(thing, property, literal);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Function for use in unit tests to mock a [[Thing]] with a given URL.
 *
 * Warning: do not use this function in actual production code.
 * This function initialises a new empty Thing and sets its URL to a given URL.
 * This is useful to mock a Thing in tests of code that call e.g.
 * [[asUrl]].
 *
 * @param url The URL that the mocked Thing pretends identifies it.
 * @returns A new Thing, pretending to be identified by the given URL.
 * @since 0.2.0
 */
function mockThingFrom(url) {
    const thing = Object.assign(dataset(), {
        internal_url: internal_toIriString(url),
    });
    return thing;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns an Agent's explicitly-granted Access Modes for the given Resource.
 *
 * The function does not return Access Modes granted indirectly to the Agent through other
 * ACL rules, e.g., public or group-specific permissions.
 *
 * @param resourceInfo Information about the Resource to which the given Agent may have been granted access.
 * @param agent WebID of the Agent for which to retrieve what access it has to the Resource.
 * @returns Access Modes that have been explicitly granted to the Agent for the given Resource, or `null` if it could not be determined (e.g. because the current user does not have Control access to a given Resource or its Container).
 */
function getAgentAccess(resourceInfo, agent) {
    if (hasResourceAcl(resourceInfo)) {
        return getAgentResourceAccess(resourceInfo.internal_acl.resourceAcl, agent);
    }
    if (hasFallbackAcl(resourceInfo)) {
        return getAgentDefaultAccess(resourceInfo.internal_acl.fallbackAcl, agent);
    }
    return null;
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 * Returns all explicitly-granted Access Modes per Agent for the given Resource.
 *
 * The function does not return Access Modes granted indirectly to Agents through other
 * ACL rules, e.g., public or group-specific permissions.
 *
 * @param resourceInfo Information about the Resource to which Agents may have been granted access.
 * @returns Access Modes per Agent that have been explicitly granted for the given Resource, or `null` if it could not be determined (e.g. because the current user does not have Control access to a given Resource or its Container).
 */
function getAgentAccessAll(resourceInfo) {
    if (hasResourceAcl(resourceInfo)) {
        const resourceAcl = getResourceAcl(resourceInfo);
        return getAgentResourceAccessAll(resourceAcl);
    }
    if (hasFallbackAcl(resourceInfo)) {
        const fallbackAcl = getFallbackAcl(resourceInfo);
        return getAgentDefaultAccessAll(fallbackAcl);
    }
    return null;
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes explicitly granted to an Agent for the Resource
 * associated with an ACL (Access ControlList).
 *
 * The function does not return:
 *
 * - Access Modes granted indirectly to the Agent through other ACL rules, e.g., public or group-specific permissions.
 * - Access Modes granted to the Agent for the child Resources if the associated Resource is a Container (see [[getAgentDefaultAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains ACL rules.
 * @param agent WebID of the Agent for which to retrieve what access it has to the Resource.
 * @returns Access Modes that have been explicitly granted to an Agent for the Resource associated with an ACL SolidDataset.
 */
function getAgentResourceAccess(aclDataset, agent) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getResourceAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const agentResourceRules = getAgentAclRulesForAgent(resourceRules, agent);
    const agentAccessModes = agentResourceRules.map(internal_getAccess);
    return internal_combineAccessModes(agentAccessModes);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the explicitly granted Access Modes per Agent for the Resource associated
 * with an ACL (Access Control List).
 *
 * The function does not return:
 *
 * - Access Modes granted indirectly to Agents through other ACL rules, e.g., public or group-specific permissions.
 * - Access Modes granted to Agents for the child Resources if the associated Resource is a Container.
 *
 * @param aclDataset The SolidDataset that contains ACL rules.
 * @returns Access Modes per Agent that have been explicitly granted for the Resource associated with an ACL SolidDataset.
 */
function getAgentResourceAccessAll(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getResourceAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const agentResourceRules = getAgentAclRules(resourceRules);
    return getAccessByAgent(agentResourceRules);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 * Modifies the resource ACL (Access Control List) to set the Access Modes for the given Agent.
 * Specifically, the function returns a new resource ACL initialised with the given ACL and
 * new rules for the Agent's access.
 *
 * If rules for Agent's access already exist in the given ACL, in the returned ACL,
 * they are replaced by the new rules.
 *
 * This function does not modify:
 *
 * - Access Modes granted indirectly to Agents through other ACL rules, e.g., public or group-specific permissions.
 * - Access Modes granted to Agents for the child Resources if the associated Resource is a Container.
 * - The original ACL.
 *
 * @param aclDataset The SolidDataset that contains Access-Control List rules.
 * @param agent The Agent to grant specific Access Modes.
 * @param access The Access Modes to grant to the Agent for the Resource.
 * @returns A new resource ACL initialised with the given `aclDataset` and `access` for the `agent`.
 */
function setAgentResourceAccess(aclDataset, agent, access) {
    // First make sure that none of the pre-existing rules in the given ACL SolidDataset
    // give the Agent access to the Resource:
    let filteredAcl = aclDataset;
    getThingAll(aclDataset).forEach((aclRule) => {
        // Obtain both the Rule that no longer includes the given Agent,
        // and a new Rule that includes all ACL Quads
        // that do not pertain to the given Agent-Resource combination.
        // Note that usually, the latter will no longer include any meaningful statements;
        // we'll clean them up afterwards.
        const [filteredRule, remainingRule] = removeAgentFromRule(aclRule, agent, aclDataset.internal_accessTo, "resource");
        filteredAcl = setThing(filteredAcl, filteredRule);
        filteredAcl = setThing(filteredAcl, remainingRule);
    });
    // Create a new Rule that only grants the given Agent the given Access Modes:
    let newRule = internal_initialiseAclRule(access);
    newRule = setIri(newRule, acl.accessTo, aclDataset.internal_accessTo);
    newRule = setIri(newRule, acl.agent, agent);
    const updatedAcl = setThing(filteredAcl, newRule);
    // Remove any remaining Rules that do not contain any meaningful statements:
    const cleanedAcl = internal_removeEmptyAclRules(updatedAcl);
    return cleanedAcl;
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns an Agent's Access Modes explicitly granted for the children of the
 * Container associated with the given ACL (Access Control List).
 *
 * The function does not return:
 * - Access Modes granted indirectly to the Agent through other ACL rules, e.g. public or group-specific permissions.
 * - Access Modes granted to the Agent for the Container Resource itself (see [[getAgentResourceAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access-Control List rules for a certain Container.
 * @param agent WebID of the Agent for which to retrieve what access it has to the Container's children.
 * @returns Access Modes that have been explicitly granted to an Agent for the children of the Container associated with the given ACL.
 */
function getAgentDefaultAccess(aclDataset, agent) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getDefaultAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const agentResourceRules = getAgentAclRulesForAgent(resourceRules, agent);
    const agentAccessModes = agentResourceRules.map(internal_getAccess);
    return internal_combineAccessModes(agentAccessModes);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes, per Agent, that have been explicitly granted for the children
 * of the Container associated with the given ACL (Access Control List).
 *
 * The function does not return:
 *
 * - Access Modes granted indirectly to the Agents through other ACL rules, e.g. public or group-specific permissions.
 * - Access Modes granted to the Agents for the Container Resource itself (see [[getAgentResourceAccessAll]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access-Control List rules.
 * @returns Access Modes, per Agent, that have been explicitly granted for the children of the Container associated with the given ACL.
 */
function getAgentDefaultAccessAll(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getDefaultAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const agentResourceRules = getAgentAclRules(resourceRules);
    return getAccessByAgent(agentResourceRules);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Modifies the default ACL (Access Control List) to set an Agent's Access Modes for the Container's children.
 * Specifically, the function returns a new default ACL initialised with the given ACL and
 * new rules for the Agent's access.
 *
 * If rules already exist for the Agent in the given ACL, in the returned ACL, they are replaced by the new rules.
 *
 * This function does not modify:
 * - Access Modes granted indirectly to the Agent through other ACL rules, e.g., public or group-specific permissions.
 * - Access Modes granted to the Agent for the Container Resource itself.
 * - The original ACL.
 *
 * @param aclDataset The SolidDataset that contains Access-Control List rules.
 * @param agent The Agent to grant specific Access Modes.
 * @param access The Access Modes to grant to the Agent.
 * @returns A new default ACL initialised with the given `aclDataset` and `access` for the `agent`.
 */
function setAgentDefaultAccess(aclDataset, agent, access) {
    // First make sure that none of the pre-existing rules in the given ACL SolidDataset
    // give the Agent default access to the Resource:
    let filteredAcl = aclDataset;
    getThingAll(aclDataset).forEach((aclRule) => {
        // Obtain both the Rule that no longer includes the given Agent,
        // and a new Rule that includes all ACL Quads
        // that do not pertain to the given Agent-Resource default combination.
        // Note that usually, the latter will no longer include any meaningful statements;
        // we'll clean them up afterwards.
        const [filteredRule, remainingRule] = removeAgentFromRule(aclRule, agent, aclDataset.internal_accessTo, "default");
        filteredAcl = setThing(filteredAcl, filteredRule);
        filteredAcl = setThing(filteredAcl, remainingRule);
    });
    // Create a new Rule that only grants the given Agent the given default Access Modes:
    let newRule = internal_initialiseAclRule(access);
    newRule = setIri(newRule, acl.default, aclDataset.internal_accessTo);
    newRule = setIri(newRule, acl.agent, agent);
    const updatedAcl = setThing(filteredAcl, newRule);
    // Remove any remaining Rules that do not contain any meaningful statements:
    const cleanedAcl = internal_removeEmptyAclRules(updatedAcl);
    return cleanedAcl;
}
function getAgentAclRulesForAgent(aclRules, agent) {
    return internal_getAclRulesForIri(aclRules, agent, acl.agent);
}
function getAgentAclRules(aclRules) {
    return aclRules.filter(isAgentAclRule);
}
function isAgentAclRule(aclRule) {
    return getIri(aclRule, acl.agent) !== null;
}
/**
 * Given an ACL Rule, returns two new ACL Rules that cover all the input Rule's use cases,
 * except for giving the given Agent access to the given Resource.
 *
 * @param rule The ACL Rule that should no longer apply for a given Agent to a given Resource.
 * @param agent The Agent that should be removed from the Rule for the given Resource.
 * @param resourceIri The Resource to which the Rule should no longer apply for the given Agent.
 * @returns A tuple with the original ACL Rule without the given Agent, and a new ACL Rule for the given Agent for the remaining Resources, respectively.
 */
function removeAgentFromRule(rule, agent, resourceIri, ruleType) {
    // If the existing Rule does not apply to the given Agent, we don't need to split up.
    // Without this check, we'd be creating a new rule for the given Agent (ruleForOtherTargets)
    // that would give it access it does not currently have:
    if (!getIriAll(rule, acl.agent).includes(agent)) {
        const emptyRule = internal_initialiseAclRule({
            read: false,
            append: false,
            write: false,
            control: false,
        });
        return [rule, emptyRule];
    }
    // The existing rule will keep applying to Agents other than the given one:
    const ruleWithoutAgent = removeIri(rule, acl.agent, agent);
    // The agent might have been given other access in the existing rule, so duplicate it...
    let ruleForOtherTargets = internal_duplicateAclRule(rule);
    // ...but remove access to the original Resource...
    ruleForOtherTargets = removeIri(ruleForOtherTargets, ruleType === "resource" ? acl.accessTo : acl.default, resourceIri);
    // Prevents the legacy predicate 'acl:defaultForNew' to lead to privilege escalation
    if (ruleType === "default") {
        ruleForOtherTargets = removeIri(ruleForOtherTargets, acl.defaultForNew, resourceIri);
    }
    // ...and only apply the new Rule to the given Agent (because the existing Rule covers the others):
    ruleForOtherTargets = setIri(ruleForOtherTargets, acl.agent, agent);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.agentClass);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.agentGroup);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.origin);
    return [ruleWithoutAgent, ruleForOtherTargets];
}
function getAccessByAgent(aclRules) {
    return internal_getAccessByIri(aclRules, acl.agent);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 * Returns a Group's explicity-granted Access Modes for a given Resource.
 *
 * The function does not return Access Modes granted indirectly to the Group through other
 * ACL rules, e.g., public permissions.
 *
 * @param resourceInfo Information about the Resource to which the given Group may have been granted access.
 * @param group URL of the Group for which to retrieve what access it has to the Resource.
 * @returns Access Modes that have been explicitly granted to the `group` for the given Resource, or `null` if it could not be determined (e.g. because the current user does not have Control Access to a given Resource or its Container).
 */
function getGroupAccess(resourceInfo, group) {
    if (hasResourceAcl(resourceInfo)) {
        return getGroupResourceAccess(resourceInfo.internal_acl.resourceAcl, group);
    }
    if (hasFallbackAcl(resourceInfo)) {
        return getGroupDefaultAccess(resourceInfo.internal_acl.fallbackAcl, group);
    }
    return null;
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns all explicitly-granted Access Modes per Group for the given Resource.
 *
 * The function does not return Access Modes granted indirectly to the Group through other
 * ACL rules, e.g., public permissions.
 *
 * @param resourceInfo Information about the Resource to which the given Group may have been granted access.
 * @returns Access Modes per Group that have been explicitly granted for the given Resource, or `null` if it could not be determined (e.g. because the current user does not have Control Access to a given Resource or its Container).
 */
function getGroupAccessAll(resourceInfo) {
    if (hasResourceAcl(resourceInfo)) {
        const resourceAcl = getResourceAcl(resourceInfo);
        return getGroupResourceAccessAll(resourceAcl);
    }
    if (hasFallbackAcl(resourceInfo)) {
        const fallbackAcl = getFallbackAcl(resourceInfo);
        return getGroupDefaultAccessAll(fallbackAcl);
    }
    return null;
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes explicitly granted to a Group for the Resource
 * associated with an ACL (Access Control List).
 *
 * The function does not return:
 * - Access Modes granted indirectly to the Group through other ACL rules, e.g., public permissions.
 * - Access Modes granted to the Group for the child Resources if the associated Resource is a Container
 *   (see [[getGroupDefaultAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules.
 * @param group URL of the Group for which to retrieve what access it has to the Resource.
 * @returns Access Modes explicitly granted to a Group for the Resource associated with an ACL.
 */
function getGroupResourceAccess(aclDataset, group) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getResourceAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const groupResourceRules = getGroupAclRuleForGroup(resourceRules, group);
    const groupAccessModes = groupResourceRules.map(internal_getAccess);
    return internal_combineAccessModes(groupAccessModes);
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the explicitly granted Access Modes per Group for the Resource associated
 * with an ACL (Access Control List).
 *
 * The function does not return:
 * - Access Modes granted indirectly to the Group through other ACL rules, e.g., public permissions.
 * - Access Modes granted to Groups for the child Resources if the associated Resource is a Container.
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules.
 * @returns Access Modes per Group that have been explicitly granted for the Resource associated with an ACL.
 */
function getGroupResourceAccessAll(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getResourceAclRulesForResource(allRules, aclDataset.internal_accessTo);
    return getAccessByGroup(resourceRules);
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns a Group's Access Modes explicitly granted for the children of the
 * Container associated with an ACL (Access ControlList).
 *
 * The function does not return:
 * - Access Modes granted indirectly to the Group through other ACL rules, e.g. public permissions.
 * - Access Modes granted to the Group for the Container Resource itself (see [[getGroupResourceAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains ACL rules for a certain Container.
 * @param group URL of the Group for which to retrieve what access it has to the child Resources of the given Container.
 * @returns Access Modes that have been explicitly granted to the Group for the children of the Container associated with the given ACL.
 */
function getGroupDefaultAccess(aclDataset, group) {
    const allRules = internal_getAclRules(aclDataset);
    const defaultRules = internal_getDefaultAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const groupDefaultRules = getGroupAclRuleForGroup(defaultRules, group);
    const groupAccessModes = groupDefaultRules.map(internal_getAccess);
    return internal_combineAccessModes(groupAccessModes);
}
/**
 * ```{note} This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes, per Group, that have been explicitly granted for the children
 * of the Container associated with the given ACL (Access Control List).
 *
 * The function does not return:
 * - Access Modes granted indirectly to the Groups through other ACL rules, e.g. public permissions.
 * - Access Modes granted to the Groups for the Container Resource itself (see [[getGroupResourceAccessAll]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules for a certain Container.
 * @returns Access Modes per Group that have been explicitly granted for the children of the Container associated with the given ACL SolidDataset.
 */
function getGroupDefaultAccessAll(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const defaultRules = internal_getDefaultAclRulesForResource(allRules, aclDataset.internal_accessTo);
    return getAccessByGroup(defaultRules);
}
function getGroupAclRuleForGroup(rules, group) {
    return internal_getAclRulesForIri(rules, group, acl.agentGroup);
}
function getAccessByGroup(aclRules) {
    return internal_getAccessByIri(aclRules, acl.agentGroup);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes granted to the public in general for a Resource.
 *
 * This function does not return Access Modes granted to specific Agents
 * through other ACL (Access Control List) rules, e.g., agent- or group-specific permissions.
 *
 * @param resourceInfo Information about the Resource to which the given Agent may have been granted access.
 * @returns Access Modes granted to the public in general for the Resource, or `null` if it could not be determined (e.g. because the current user does not have Control Access to a given Resource or its Container).
 */
function getPublicAccess(resourceInfo) {
    if (hasResourceAcl(resourceInfo)) {
        return getPublicResourceAccess(resourceInfo.internal_acl.resourceAcl);
    }
    if (hasFallbackAcl(resourceInfo)) {
        return getPublicDefaultAccess(resourceInfo.internal_acl.fallbackAcl);
    }
    return null;
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes granted to the public in general for the Resource
 * associated with an ACL (Access Control List).
 *
 * This function does not return:
 * - Access Modes granted to specific Agents through other ACL rules, e.g., agent- or group-specific permissions.
 * - Access Modes to child Resources if the associated Resource is a Container (see [[getPublicDefaultAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules.
 * @returns Access Modes granted to the public in general for the Resource associated with the `aclDataset`.
 */
function getPublicResourceAccess(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getResourceAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const publicResourceRules = getClassAclRulesForClass(resourceRules, foaf.Agent);
    const publicAccessModes = publicResourceRules.map(internal_getAccess);
    return internal_combineAccessModes(publicAccessModes);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Returns the Access Modes granted to the public in general for the child Resources
 * of the Container associated with an ACL (Access Control List).
 *
 * This function does not return:
 * - Access Modes granted to Agents through other ACL rules, e.g., agent- or group-specific permissions.
 * - Access Modes to the Container Resource itself (see [[getPublicResourceAccess]] instead).
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules for a certain Container.
 * @returns Access Modes granted to the public in general for the children of the Container associated with the given `aclDataset`.
 */
function getPublicDefaultAccess(aclDataset) {
    const allRules = internal_getAclRules(aclDataset);
    const resourceRules = internal_getDefaultAclRulesForResource(allRules, aclDataset.internal_accessTo);
    const publicResourceRules = getClassAclRulesForClass(resourceRules, foaf.Agent);
    const publicAccessModes = publicResourceRules.map(internal_getAccess);
    return internal_combineAccessModes(publicAccessModes);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Modifies the resource ACL (Access Control List) to set the Access Modes for the public.
 * Specifically, the function returns a new resource ACL (Access Control List) initialised
 * with the given resource ACL and new rules for the given public access.
 *
 * If rules for public access already exist in the given ACL, in the *returned* ACL,
 * they are replaced by the new rules.
 *
 * This function does not modify:
 * - Access Modes granted to Agents through other ACL rules, e.g., agent- or group-specific permissions.
 * - Access Modes to child Resources if the associated Resource is a Container.
 * - The original ACL.
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules.
 * @param access The Access Modes to grant to the public.
 * @returns A new resource ACL initialised with the given `aclDataset` and public `access`.
 */
function setPublicResourceAccess(aclDataset, access) {
    // First make sure that none of the pre-existing rules in the given ACL SolidDataset
    // give the public access to the Resource:
    let filteredAcl = aclDataset;
    getThingAll(aclDataset).forEach((aclRule) => {
        // Obtain both the Rule that no longer includes the public,
        // and a new Rule that includes all ACL Quads
        // that do not pertain to the given Public-Resource combination.
        // Note that usually, the latter will no longer include any meaningful statements;
        // we'll clean them up afterwards.
        const [filteredRule, remainingRule] = removePublicFromRule(aclRule, aclDataset.internal_accessTo, "resource");
        filteredAcl = setThing(filteredAcl, filteredRule);
        filteredAcl = setThing(filteredAcl, remainingRule);
    });
    // Create a new Rule that only grants the public the given Access Modes:
    let newRule = internal_initialiseAclRule(access);
    newRule = setIri(newRule, acl.accessTo, aclDataset.internal_accessTo);
    newRule = setIri(newRule, acl.agentClass, foaf.Agent);
    const updatedAcl = setThing(filteredAcl, newRule);
    // Remove any remaining Rules that do not contain any meaningful statements:
    return internal_removeEmptyAclRules(updatedAcl);
}
/**
 * ```{note}
 * This function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Modifies the default ACL (Access Control List) to set the public's default Access Modes
 * to child resources. Specifically, the function returns a new default ACL initialised
 * with the given default ACL and new rules for the given public access.
 *
 * If rules for public access already exist in the given ACL, in the *returned* ACL,
 * they are replaced by the new rules.
 *
 * This function does not modify:
 * - Access Modes granted to Agents through other ACL rules, e.g., agent- or group-specific permissions.
 * - Access Modes to Container Resource itself.
 * - The original ACL.
 *
 * @param aclDataset The SolidDataset that contains Access Control List rules.
 * @param access The Access Modes to grant to the public.
 * @returns A new default ACL initialised with the given `aclDataset` and public `access`.
 */
function setPublicDefaultAccess(aclDataset, access) {
    // First make sure that none of the pre-existing rules in the given ACL SolidDataset
    // give the public default access to the Resource:
    let filteredAcl = aclDataset;
    getThingAll(aclDataset).forEach((aclRule) => {
        // Obtain both the Rule that no longer includes the public,
        // and a new Rule that includes all ACL Quads
        // that do not pertain to the given Public-Resource default combination.
        // Note that usually, the latter will no longer include any meaningful statements;
        // we'll clean them up afterwards.
        const [filteredRule, remainingRule] = removePublicFromRule(aclRule, aclDataset.internal_accessTo, "default");
        filteredAcl = setThing(filteredAcl, filteredRule);
        filteredAcl = setThing(filteredAcl, remainingRule);
    });
    // Create a new Rule that only grants the public the given default Access Modes:
    let newRule = internal_initialiseAclRule(access);
    newRule = setIri(newRule, acl.default, aclDataset.internal_accessTo);
    newRule = setIri(newRule, acl.agentClass, foaf.Agent);
    const updatedAcl = setThing(filteredAcl, newRule);
    // Remove any remaining Rules that do not contain any meaningful statements:
    const cleanedAcl = internal_removeEmptyAclRules(updatedAcl);
    return cleanedAcl;
}
/**
 * Given an ACL Rule, return two new ACL Rules that cover all the input Rule's use cases,
 * except for giving the public access to the given Resource.
 *
 * @param rule The ACL Rule that should no longer apply for the public to a given Resource.
 * @param resourceIri The Resource to which the Rule should no longer apply for the public.
 * @returns A tuple with the original ACL Rule sans the public, and a new ACL Rule for the public for the remaining Resources, respectively.
 */
function removePublicFromRule(rule, resourceIri, ruleType) {
    // If the existing Rule does not apply to the given Agent, we don't need to split up.
    // Without this check, we'd be creating a new rule for the given Agent (ruleForOtherTargets)
    // that would give it access it does not currently have:
    if (!getIriAll(rule, acl.agentClass).includes(foaf.Agent)) {
        const emptyRule = internal_initialiseAclRule({
            read: false,
            append: false,
            write: false,
            control: false,
        });
        return [rule, emptyRule];
    }
    // The existing rule will keep applying to other Agent Classes:
    const ruleWithoutPublic = removeIri(rule, acl.agentClass, foaf.Agent);
    // The public might have been given other access in the existing rule, so duplicate it...
    let ruleForOtherTargets = internal_duplicateAclRule(rule);
    // ...but remove access to the original Resource...
    ruleForOtherTargets = removeIri(ruleForOtherTargets, ruleType === "resource" ? acl.accessTo : acl.default, resourceIri);
    // Prevents the legacy predicate 'acl:defaultForNew' to lead to privilege escalation
    if (ruleType === "default") {
        ruleForOtherTargets = removeIri(ruleForOtherTargets, acl.defaultForNew, resourceIri);
    }
    // ...and only apply the new Rule to the Public (because the existing Rule covers other Agents):
    ruleForOtherTargets = setIri(ruleForOtherTargets, acl.agentClass, foaf.Agent);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.agent);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.agentGroup);
    ruleForOtherTargets = removeAll(ruleForOtherTargets, acl.origin);
    return [ruleWithoutPublic, ruleForOtherTargets];
}
function getClassAclRulesForClass(aclRules, agentClass) {
    return aclRules.filter((rule) => appliesToClass(rule, agentClass));
}
function appliesToClass(aclRule, agentClass) {
    return getIriAll(aclRule, acl.agentClass).includes(agentClass);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{warning}
 * Do not use this function in production code. For use in **unit tests** that
 * require a [[SolidDataset]] with a resource ACL (Access Control List).
 * ```
 *
 * Initialises a new empty ACL and attaches it to a given [[SolidDataset]] for use
 * in **unit tests**; e.g., unit tests that call [[getResourceAcl]].
 *
 * @param resource The Resource to mock up with a new resource ACL.
 * @returns The input Resource with an empty resource ACL attached.
 * @since 0.2.0
 */
function addMockResourceAclTo(resource) {
    var _a;
    const aclUrl = (_a = resource.internal_resourceInfo.aclUrl) !== null && _a !== void 0 ? _a : "https://your.pod/mock-acl.ttl";
    const resourceWithAclUrl = Object.assign(internal_cloneResource(resource), {
        internal_resourceInfo: Object.assign(Object.assign({}, resource.internal_resourceInfo), { aclUrl: aclUrl }),
    });
    const aclDataset = createAcl(resourceWithAclUrl);
    const resourceWithResourceAcl = Object.assign(resourceWithAclUrl, {
        internal_acl: {
            resourceAcl: aclDataset,
            fallbackAcl: null,
        },
    });
    return resourceWithResourceAcl;
}
/**
 *
 * ```{warning}
 * Do not use this function in production code.  For use in **unit tests** that require a
 * [[SolidDataset]] with a fallback ACL (Access Control List).
 * ```
 *
 * Initialises a new empty fallback ACL and attaches it to a given [[SolidDataset]] for use
 * in **unit tests**; e.g., unit tests that call [[getFallbackAcl]].
 *
 * @param resource The Resource to mock up with new fallback ACL.
 * @returns The input Resource with an empty fallback ACL attached.
 * @since 0.2.0
 */
function addMockFallbackAclTo(resource) {
    const containerUrl = internal_getContainerPath(getSourceIri(resource));
    const aclUrl = containerUrl + ".acl";
    const mockContainer = setMockAclUrl(mockContainerFrom(containerUrl), aclUrl);
    const aclDataset = createAcl(mockContainer);
    const resourceWithFallbackAcl = Object.assign(internal_cloneResource(resource), {
        internal_acl: {
            resourceAcl: null,
            fallbackAcl: aclDataset,
        },
    });
    return resourceWithFallbackAcl;
}
function setMockAclUrl(resource, aclUrl) {
    const resourceWithAclUrl = Object.assign(internal_cloneResource(resource), {
        internal_resourceInfo: Object.assign(Object.assign({}, resource.internal_resourceInfo), { aclUrl: aclUrl }),
    });
    return resourceWithAclUrl;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Given a Resource, check whether it is governed by Access Policies.
 * (Specifically, a Resource that is governed by Access Policies will refer to exactly one Access
 * Control Resource, and expose that to users who are allowed to see or modify access to the given
 * Resource.)
 *
 * @param resource Resource which may or may not be governed by Access Policies.
 * @returns True if the Resource refers to an Access Control Resource and is hence governed by Access Policies, or false if it does not.
 */
function hasLinkedAcr(resource) {
    return (hasServerResourceInfo(resource) &&
        Array.isArray(resource.internal_resourceInfo.linkedResources[acp.accessControl]) &&
        resource.internal_resourceInfo.linkedResources[acp.accessControl].length ===
            1);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Initialise a new [[Control]].
 */
function createControl(options) {
    let control = createThing(options);
    control = setIri(control, rdf.type, acp.AccessControl);
    return control;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Find an [[Control]] with a given URL in a given Resource with an Access Control Resource.
 *
 * @returns The requested Access Control, or `null` if it could not be found.
 */
function getControl(withAccessControlResource, url, options) {
    const acr = internal_getAcr(withAccessControlResource);
    const foundThing = getThing(acr, url, options);
    if (foundThing === null ||
        !getIriAll(foundThing, rdf.type).includes(acp.AccessControl)) {
        return null;
    }
    return foundThing;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get all [[Control]]s in the Access Control Resource of a given Resource.
 */
function getControlAll(withAccessControlResource, options) {
    const acr = internal_getAcr(withAccessControlResource);
    const foundThings = getThingAll(acr, options);
    return foundThings.filter((foundThing) => getIriAll(foundThing, rdf.type).includes(acp.AccessControl));
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Insert an [[Control]] into the [[AccessControlResource]] of a Resource, replacing previous
 * instances of that Access Control.
 *
 * @param withAccessControlResource A Resource with the Access Control Resource into which to insert an Access Control.
 * @param control The Control to insert into the Access Control Resource.
 * @returns The given Resource with a new Access Control Resource equal to the original Access Control Resource, but with the given Access Control.
 */
function setControl(withAccessControlResource, control) {
    const acr = internal_getAcr(withAccessControlResource);
    const updatedAcr = setThing(acr, control);
    const updatedResource = internal_setAcr(withAccessControlResource, updatedAcr);
    return updatedResource;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove an [[Control]] from the [[AccessControlResource]] of a Resource.
 *
 * @param withAccessControlResource A Resource with the Access Control Resource from which to remove an Access Control.
 * @param control The [[Control]] to remove from the given Access Control Resource.
 * @returns The given Resource with a new Access Control Resource equal to the original Access Control Resource, excluding the given Access Control.
 */
function removeControl(withAccessControlResource, control) {
    const acr = internal_getAcr(withAccessControlResource);
    const updatedAcr = removeThing(acr, control);
    const updatedResource = internal_setAcr(withAccessControlResource, updatedAcr);
    return updatedResource;
}
/** @hidden */
function internal_getAcr(resource) {
    if (!hasAccessibleAcr(resource)) {
        throw new Error(`Cannot work with Access Controls on a Resource (${getSourceUrl(resource)}) that does not have an Access Control Resource.`);
    }
    return resource.internal_acp.acr;
}
/** @hidden */
function internal_setAcr(resource, acr) {
    return Object.assign(internal_cloneResource(resource), {
        internal_acp: Object.assign(Object.assign({}, resource.internal_acp), { acr: acr }),
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a [[Policy]] to an Access Control Resource such that that [[Policy]] applies to the Access
 * Control Resource itself, rather than the Resource it governs.
 *
 * @param resourceWithAcr The [[Control]] to which the ACR Policy should be added.
 * @param policyUrl URL of the Policy that should apply to the given Access Control Resource.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given ACR Policy added to it.
 */
function addAcrPolicyUrl(resourceWithAcr, policyUrl) {
    var _a;
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    let acrThing = (_a = getThing(acr, acrUrl)) !== null && _a !== void 0 ? _a : createThing({ url: acrUrl });
    acrThing = addIri(acrThing, acp.access, policyUrl);
    const updatedAcr = setThing(acr, acrThing);
    const updatedResource = internal_setAcr(resourceWithAcr, updatedAcr);
    return updatedResource;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a [[Policy]] to an Access Control Resource such that that [[Policy]] applies to the Access
 * Control Resources of child Resources.
 *
 * @param resourceWithAcr The [[Control]] to which the ACR Policy should be added.
 * @param policyUrl URL of the Policy that should apply to the given Access Control Resources of children of the Resource.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given ACR Policy added to it.
 */
function addMemberAcrPolicyUrl(resourceWithAcr, policyUrl) {
    var _a;
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    let acrThing = (_a = getThing(acr, acrUrl)) !== null && _a !== void 0 ? _a : createThing({ url: acrUrl });
    acrThing = addIri(acrThing, acp.accessMembers, policyUrl);
    const updatedAcr = setThing(acr, acrThing);
    const updatedResource = internal_setAcr(resourceWithAcr, updatedAcr);
    return updatedResource;
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the URLs of the Access Policies that apply to an Access Control Resource itself, rather than
 * to the Resource it governs.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource of which to get the URLs of the Policies that govern access to it.
 * @returns URLs of the Policies that govern access to the given Access Control Resource.
 */
function getAcrPolicyUrlAll(resourceWithAcr) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return [];
    }
    return getIriAll(acrThing, acp.access);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the URLs of the Access Policies that apply to the Access Control Resources of the Resource's
 * children.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource of which to get the URLs of the Policies that govern access to its children.
 * @returns URLs of the Policies that govern access to the Access Control Resources of the given Resource's children.
 */
function getMemberAcrPolicyUrlAll(resourceWithAcr) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return [];
    }
    return getIriAll(acrThing, acp.accessMembers);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Stop the URL of a given [[Policy]] from applying to an Access Control Resource itself.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource to which the given URL of a Policy should no longer apply.
 * @param policyUrl The URL of the Policy that should no longer apply.
 * @returns A new [[Control]] equal to the given Access Control, but with the given ACR Policy removed from it.
 */
function removeAcrPolicyUrl(resourceWithAcr, policyUrl) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return resourceWithAcr;
    }
    const updatedAcrThing = removeIri(acrThing, acp.access, policyUrl);
    const updatedAcr = setThing(acr, updatedAcrThing);
    return internal_setAcr(resourceWithAcr, updatedAcr);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Stop the URL of a given [[Policy]] from applying to the Access Control Resources of the
 * Resource's children.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource to whose children's ACRs the given URL of a Policy should no longer apply.
 * @param policyUrl The URL of the Policy that should no longer apply.
 * @returns A new Access Control equal to the given Access Control, but with the given Member ACR Policy removed from it.
 */
function removeMemberAcrPolicyUrl(resourceWithAcr, policyUrl) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return resourceWithAcr;
    }
    const updatedAcrThing = removeIri(acrThing, acp.accessMembers, policyUrl);
    const updatedAcr = setThing(acr, updatedAcrThing);
    return internal_setAcr(resourceWithAcr, updatedAcr);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Stop all URL of Access Policies from applying to an Access Control Resource itself.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource to which no more Policies should apply.
 * @returns A new [[Control]] equal to the given [[Control]], but without any Policy applying to it.
 */
function removeAcrPolicyUrlAll(resourceWithAcr) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return resourceWithAcr;
    }
    const updatedAcrThing = removeAll(acrThing, acp.access);
    const updatedAcr = setThing(acr, updatedAcrThing);
    return internal_setAcr(resourceWithAcr, updatedAcr);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Stop all URL of Access Policies from applying to the Access Control Resources of the Resource's
 * children.
 *
 * @param resourceWithAcr The Resource with the Access Control Resource that should no longer apply Policies to its children's ACRs.
 * @returns A new [[Control]] equal to the given [[Control]], but without any Policy applying to its children's ACRs.
 */
function removeMemberAcrPolicyUrlAll(resourceWithAcr) {
    const acr = internal_getAcr(resourceWithAcr);
    const acrUrl = getSourceUrl(acr);
    const acrThing = getThing(acr, acrUrl);
    if (acrThing === null) {
        return resourceWithAcr;
    }
    const updatedAcrThing = removeAll(acrThing, acp.accessMembers);
    const updatedAcr = setThing(acr, updatedAcrThing);
    return internal_setAcr(resourceWithAcr, updatedAcr);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a [[Policy]] to an [[Control]] such that that Policy applies to the Resource to which
 * the [[Control]] is linked.
 *
 * @param accessControl The [[Control]] to which the Policy should be added.
 * @param policyUrl URL of the Policy that should apply to the Resource to which the [[Control]] is linked.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given policy added to it.
 */
function addPolicyUrl(accessControl, policyUrl) {
    return addIri(accessControl, acp.apply, policyUrl);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get all Policies that apply to the Resource to which the given [[Control]] is linked, and
 * which can be removed by anyone with Write access to the Access Control Resource that contains the
 * [[Control]].
 *
 * @param accessControl The [[Control]] of which to get the Policies.
 * @returns The Policies that are listed in this [[Control]] as applying to the Resource it is linked to, and as removable by anyone with Write access to the Access Control Resource.
 */
function getPolicyUrlAll(accessControl) {
    return getIriAll(accessControl, acp.apply);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove a given Policy that applies to the Resource to which the given [[Control]] is linked,
 * and which can be removed by anyone with Write access to the Access Control Resource that contains
 * the Access Control.
 *
 * @param accessControl The [[Control]] of which to remove the Policies.
 * @param policyUrl URL of the Policy that should no longer apply to the Resource to which the [[Control]] is linked.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given Policy removed from it.
 */
function removePolicyUrl(accessControl, policyUrl) {
    return removeIri(accessControl, acp.apply, policyUrl);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove all Policies that apply to the Resource to which the given [[Control]] is linked, and
 * which can be removed by anyone with Write access to the Access Control Resource that contains the
 * [[Control]].
 *
 * @param accessControl The [[Control]] of which to remove the Policies.
 * @returns A new [[Control]] equal to the given [[Control]], but with all Policies removed from it.
 */
function removePolicyUrlAll(accessControl) {
    return removeAll(accessControl, acp.apply);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a [[Policy]] to an [[Control]] such that that Policy applies to the children of the
 * Resource to which the [[Control]] is linked.
 *
 * @param accessControl The [[Control]] to which the Policy should be added.
 * @param policyUrl URL of the Policy that should apply to the children of the Resource to which the [[Control]] is linked.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given policy added to it as a Member Policy.
 */
function addMemberPolicyUrl(accessControl, policyUrl) {
    return addIri(accessControl, acp.applyMembers, policyUrl);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get all Policies that apply to the children of the Resource to which the given [[Control]] is
 * linked, and which can be removed by anyone with Write access to the Access Control Resource that
 * contains the [[Control]].
 *
 * @param accessControl The [[Control]] of which to get the Policies.
 * @returns The Policies that are listed in this [[Control]] as applying to the children of the Resource it is linked to, and as removable by anyone with Write access to the Access Control Resource.
 */
function getMemberPolicyUrlAll(accessControl) {
    return getIriAll(accessControl, acp.applyMembers);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove a given Policy that applies to the children of the Resource to which the given Access
 * Control is linked, and which can be removed by anyone with Write access to the Access Control
 * Resource that contains the Access Control.
 *
 * @param accessControl The [[Control]] of which to remove the Member Policy.
 * @param policyUrl URL of the Member Policy that should no longer apply to the Resource to which the [[Control]] is linked.
 * @returns A new [[Control]] equal to the given [[Control]], but with the given Member Policy removed from it.
 */
function removeMemberPolicyUrl(accessControl, policyUrl) {
    return removeIri(accessControl, acp.applyMembers, policyUrl);
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove all Policies that apply to the children of the Resource to which the given Access Control
 * is linked, and which can be removed by anyone with Write access to the Access Control Resource
 * that contains the Access Control.
 *
 * @param accessControl The [[Control]] of which to remove the Member Policies.
 * @returns A new [[Control]] equal to the given [[Control]], but with all Member Policies removed from it.
 */
function removeMemberPolicyUrlAll(accessControl) {
    return removeAll(accessControl, acp.applyMembers);
}

var acpControl = /*#__PURE__*/Object.freeze({
    __proto__: null,
    hasLinkedAcr: hasLinkedAcr,
    createControl: createControl,
    getControl: getControl,
    getControlAll: getControlAll,
    setControl: setControl,
    removeControl: removeControl,
    internal_getAcr: internal_getAcr,
    internal_setAcr: internal_setAcr,
    addAcrPolicyUrl: addAcrPolicyUrl,
    addMemberAcrPolicyUrl: addMemberAcrPolicyUrl,
    getAcrPolicyUrlAll: getAcrPolicyUrlAll,
    getMemberAcrPolicyUrlAll: getMemberAcrPolicyUrlAll,
    removeAcrPolicyUrl: removeAcrPolicyUrl,
    removeMemberAcrPolicyUrl: removeMemberAcrPolicyUrl,
    removeAcrPolicyUrlAll: removeAcrPolicyUrlAll,
    removeMemberAcrPolicyUrlAll: removeMemberAcrPolicyUrlAll,
    addPolicyUrl: addPolicyUrl,
    getPolicyUrlAll: getPolicyUrlAll,
    removePolicyUrl: removePolicyUrl,
    removePolicyUrlAll: removePolicyUrlAll,
    addMemberPolicyUrl: addMemberPolicyUrl,
    getMemberPolicyUrlAll: getMemberPolicyUrlAll,
    removeMemberPolicyUrl: removeMemberPolicyUrl,
    removeMemberPolicyUrlAll: removeMemberPolicyUrlAll
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Fetch a SolidDataset and its associated Access Control Resource (if available to the current user).
 *
 * @param url URL of the SolidDataset to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A SolidDataset and the ACR that applies to it, if available to the authenticated user.
 */
function getSolidDatasetWithAcr(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const solidDataset = yield getSolidDataset(urlString, config);
        const acp = yield fetchAcr(solidDataset, config);
        return Object.assign(solidDataset, acp);
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Fetch a file and its associated Access Control Resource (if available to the current user).
 *
 * @param url URL of the file to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A file and the ACR that applies to it, if available to the authenticated user.
 */
function getFileWithAcr(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const file = yield getFile(urlString, config);
        const acp = yield fetchAcr(file, config);
        return Object.assign(file, acp);
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Retrieve information about a Resource and its associated Access Control Resource (if available to
 * the current user), without fetching the Resource itself.
 *
 * @param url URL of the Resource about which to fetch its information.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns Metadata describing a Resource, and the ACR that applies to it, if available to the authenticated user.
 */
function getResourceInfoWithAcr(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const resourceInfo = yield getResourceInfo(urlString, config);
        const acp = yield fetchAcr(resourceInfo, config);
        return Object.assign(resourceInfo, acp);
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Fetch a SolidDataset, and:
 * - if the Resource is governed by an ACR: its associated Access Control Resource (if available to
 *                                          the current user), and all the Access Control Policies
 *                                          referred to therein, if available to the current user.
 * - if the Resource is governed by an ACL: its associated Resource ACL (if available to the current
 *                                          user), or its Fallback ACL if it does not exist.
 *
 * @param url URL of the SolidDataset to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A SolidDataset and either the ACL access data or the ACR access data, if available to the current user.
 */
function getSolidDatasetWithAccessDatasets(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const solidDataset = yield getSolidDataset(urlString, config);
        if (hasAccessibleAcl(solidDataset)) {
            const acl = yield internal_fetchAcl(solidDataset, config);
            return Object.assign(solidDataset, { internal_acl: acl });
        }
        else {
            const acr = yield fetchAcr(solidDataset, config);
            return Object.assign(solidDataset, acr);
        }
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Fetch a File, and:
 * - if the Resource is governed by an ACR: its associated Access Control Resource (if available to
 *                                          the current user), and all the Access Control Policies
 *                                          referred to therein, if available to the current user.
 * - if the Resource is governed by an ACL: its associated Resource ACL (if available to the current
 *                                          user), or its Fallback ACL if it does not exist.
 *
 * @param url URL of the File to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns A File and either the ACL access data or the ACR access data, if available to the current user.
 */
function getFileWithAccessDatasets(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const file = yield getFile(urlString, config);
        if (hasAccessibleAcl(file)) {
            const acl = yield internal_fetchAcl(file, config);
            return Object.assign(file, { internal_acl: acl });
        }
        else {
            const acr = yield fetchAcr(file, config);
            return Object.assign(file, acr);
        }
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Fetch information about a Resource, and:
 * - if the Resource is governed by an ACR: its associated Access Control Resource (if available to
 *                                          the current user), and all the Access Control Policies
 *                                          referred to therein, if available to the current user.
 * - if the Resource is governed by an ACL: its associated Resource ACL (if available to the current
 *                                          user), or its Fallback ACL if it does not exist.
 *
 * @param url URL of the Resource information about which to fetch.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 * @returns Information about a Resource and either the ACL access data or the ACR access data, if available to the current user.
 */
function getResourceInfoWithAccessDatasets(url, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const urlString = internal_toIriString(url);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const resourceInfo = yield getResourceInfo(urlString, config);
        if (hasAccessibleAcl(resourceInfo)) {
            const acl = yield internal_fetchAcl(resourceInfo, config);
            return Object.assign(resourceInfo, { internal_acl: acl });
        }
        else {
            const acr = yield fetchAcr(resourceInfo, config);
            return Object.assign(resourceInfo, acr);
        }
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Save a Resource's Access Control Resource.
 *
 * @param resource Resource with an Access Control Resource that should be saved.
 * @param options Optional parameter `options.fetch`: An alternative `fetch` function to make the HTTP request, compatible with the browser-native [fetch API](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/fetch#parameters).
 */
function saveAcrFor(resource, options = internal_defaultFetchOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        const acr = internal_getAcr(resource);
        const config = Object.assign(Object.assign({}, internal_defaultFetchOptions), options);
        const savedAcr = yield saveSolidDatasetAt(getSourceUrl(acr), acr, config);
        return internal_setAcr(resource, savedAcr);
    });
}
/**
 * @param resource Resource of which to check whether it has an Access Control Resource attached.
 * @returns Boolean representing whether the given Resource has an Access Control Resource attached for use in e.g. [[getControl]].
 */
function hasAccessibleAcr(resource) {
    return (typeof resource.internal_acp === "object" &&
        typeof resource.internal_acp.acr === "object");
}
function fetchAcr(resource, options) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hasLinkedAcr(resource)) {
            return {
                internal_acp: {
                    acr: null,
                },
            };
        }
        let acr;
        try {
            acr = yield getSolidDataset(
            // Whereas a Resource can generally have multiple linked Resources for the same relation,
            // it can only have one Access Control Resource for that ACR to be valid.
            // Hence the accessing of [0] directly:
            resource.internal_resourceInfo.linkedResources[acp.accessControl][0], options);
        }
        catch (e) {
            return {
                internal_acp: {
                    acr: null,
                },
            };
        }
        const resourceUrl = getSourceUrl(resource);
        const acrUrl = getSourceUrl(acr);
        const acrDataset = Object.assign(acr, {
            accessTo: getSourceUrl(resource),
        });
        const acpInfo = {
            internal_acp: {
                acr: acrDataset,
            },
        };
        return acpInfo;
    });
}
/**
 * ```{note} The Web Access Control specification is not yet finalised. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * To make it easy to fetch all the relevant Access Policy Resources,
 * this function returns all referenced Access Policy Resources referenced in an
 * Access Control Resource.
 * In other words, if Access Controls refer to different Policies in the same
 * Access Policy Resource, this function will only return that Access Policy
 * Resource's URL once.
 *
 * @param withAcr A Resource with an Access Control Resource attached.
 * @returns List of all unique Access Policy Resources that are referenced in the given Access Control Resource.
 */
function getReferencedPolicyUrlAll(withAcr) {
    const policyUrls = [];
    const controls = getControlAll(withAcr);
    controls.forEach((control) => {
        policyUrls.push(...getPolicyUrlAll(control).map(getResourceUrl));
        policyUrls.push(...getMemberPolicyUrlAll(control).map(getResourceUrl));
    });
    policyUrls.push(...getAcrPolicyUrlAll(withAcr).map(getResourceUrl));
    policyUrls.push(...getMemberAcrPolicyUrlAll(withAcr).map(getResourceUrl));
    const uniqueUrls = Array.from(new Set(policyUrls));
    return uniqueUrls;
}
/**
 * To verify whether two URLs are at the same location, we need to strip the hash.
 * This function does that.
 */
function getResourceUrl(urlWithHash) {
    const url = new URL(urlWithHash);
    url.hash = "";
    return url.href;
}

var acpAcp = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getSolidDatasetWithAcr: getSolidDatasetWithAcr,
    getFileWithAcr: getFileWithAcr,
    getResourceInfoWithAcr: getResourceInfoWithAcr,
    getSolidDatasetWithAccessDatasets: getSolidDatasetWithAccessDatasets,
    getFileWithAccessDatasets: getFileWithAccessDatasets,
    getResourceInfoWithAccessDatasets: getResourceInfoWithAccessDatasets,
    saveAcrFor: saveAcrFor,
    hasAccessibleAcr: hasAccessibleAcr,
    getReferencedPolicyUrlAll: getReferencedPolicyUrlAll
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Initialise a new, empty [[Policy]].
 *
 * @param url URL that identifies this Policy.
 */
function createPolicy(url) {
    const stringUrl = internal_toIriString(url);
    let policyThing = createThing({ url: stringUrl });
    policyThing = setUrl(policyThing, rdf.type, acp.Policy);
    return policyThing;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the [[Policy]] with the given URL from an [[PolicyDataset]].
 *
 * @param policyResource The Resource that contains the given Policy.
 * @param url URL that identifies this Policy.
 * @returns The requested Policy, if it exists, or `null` if it does not.
 */
function getPolicy(policyResource, url) {
    const foundThing = getThing(policyResource, url);
    if (foundThing === null || getUrl(foundThing, rdf.type) !== acp.Policy) {
        return null;
    }
    return foundThing;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get all [[Policy]]'s in a given [[PolicyDataset]].
 *
 * @param policyResource The Resource that contains Access Policies.
 */
function getPolicyAll(policyResource) {
    const foundThings = getThingAll(policyResource);
    const foundPolicies = foundThings.filter((thing) => !isThingLocal(thing) && getUrlAll(thing, rdf.type).includes(acp.Policy));
    return foundPolicies;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Remove the given [[Policy]] from the given [[PolicyDataset]].
 *
 * @param policyResource The Resource that contains Access Policies.
 * @param policy The Policy to remove from the resource.
 */
function removePolicy(policyResource, policy) {
    return removeThing(policyResource, policy);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Insert the given [[Policy]] into the given [[PolicyDataset]], replacing previous instances of that Policy.
 *
 * @param policyResource The Resource that contains Access Policies.
 * @param policy The Policy to insert into the Resource.
 * @returns A new dataset equal to the given resource, but with the given Policy.
 */
function setPolicy(policyResource, policy) {
    return setThing(policyResource, policy);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Given a [[Policy]] and a set of [[AccessModes]], return a new Policy based on the given
 * Policy, but with the given Access Modes allowed on it.
 *
 * @param policy The Policy on which to set the modes to allow.
 * @param modes Modes to allow for this Policy.
 */
function setAllowModes(policy, modes) {
    let newPolicy = removeAll(policy, acp.allow);
    if (modes.read === true) {
        newPolicy = addIri(newPolicy, acp.allow, acp.Read);
    }
    if (modes.append === true) {
        newPolicy = addIri(newPolicy, acp.allow, acp.Append);
    }
    if (modes.write === true) {
        newPolicy = addIri(newPolicy, acp.allow, acp.Write);
    }
    return newPolicy;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Given a [[Policy]], return which [[AccessModes]] it allows.
 *
 * @param policy The Policy for which you want to know the Access Modes it allows.
 */
function getAllowModes(policy) {
    const allowedModes = getIriAll(policy, acp.allow);
    return {
        read: allowedModes.includes(acp.Read),
        append: allowedModes.includes(acp.Append),
        write: allowedModes.includes(acp.Write),
    };
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Given a [[Policy]] and a set of [[AccessModes]], return a new Policy based on the given
 * Policy, but with the given Access Modes disallowed on it.
 *
 * @param policy The Policy on which to set the modes to disallow.
 * @param modes Modes to disallow for this Policy.
 */
function setDenyModes(policy, modes) {
    let newPolicy = removeAll(policy, acp.deny);
    if (modes.read === true) {
        newPolicy = addIri(newPolicy, acp.deny, acp.Read);
    }
    if (modes.append === true) {
        newPolicy = addIri(newPolicy, acp.deny, acp.Append);
    }
    if (modes.write === true) {
        newPolicy = addIri(newPolicy, acp.deny, acp.Write);
    }
    return newPolicy;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Given a [[Policy]], return which [[AccessModes]] it disallows.
 *
 * @param policy The Policy on which you want to know the Access Modes it disallows.
 */
function getDenyModes(policy) {
    const deniedModes = getIriAll(policy, acp.deny);
    return {
        read: deniedModes.includes(acp.Read),
        append: deniedModes.includes(acp.Append),
        write: deniedModes.includes(acp.Write),
    };
}

var acpPolicy = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createPolicy: createPolicy,
    getPolicy: getPolicy,
    getPolicyAll: getPolicyAll,
    removePolicy: removePolicy,
    setPolicy: setPolicy,
    setAllowModes: setAllowModes,
    getAllowModes: getAllowModes,
    setDenyModes: setDenyModes,
    getDenyModes: getDenyModes
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * NOTE: Don't export for now (i.e. if exported, should this be `isAcpRule()` so
 * as not to clash with `isAclRule()`.
 *
 * @param thing the [[Thing]] to check to see if it's an ACP rule or not
 */
function isRule(thing) {
    return getIriAll(thing, rdf.type).includes(acp.Rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a rule that refines the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is **not** present in **any** of the required rules,
 * they will not be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rule The rule to add to the policy.
 * @returns A new [[Policy]] clone of the original one, with the new rule added.
 * @since Unreleased
 */
function addRequiredRuleUrl(policy, rule) {
    return addIri(policy, acp.allOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Removes a rule that refines the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is **not** present in **any** of the required rules,
 * they will not be granted access.
 * @param policy The [[Policy]] from which the rule should be removed.
 * @param rule The rule to remove from the policy.
 * @returns A new [[Policy]] clone of the original one, with the rule removed.
 * @since Unreleased
 */
function removeRequiredRuleUrl(policy, rule) {
    return removeIri(policy, acp.allOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Overwrites the rule refining the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is **not** present in **any** of the required rules,
 * they will not be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rules The rules the policy requires.
 * @returns A new [[Policy]] clone of the original one, with the required rules replaced.
 * @since Unreleased
 */
function setRequiredRuleUrl(policy, rule) {
    return setIri(policy, acp.allOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the [[Rule]]'s required by the given [[Policy]]
 * @param policy The [[policy]] from which the rules should be read.
 * @returns A list of the required [[Rule]]'s
 * @since unreleased
 */
function getRequiredRuleUrlAll(policy) {
    return getIriAll(policy, acp.allOf);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a rule that extends the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the required rules,
 * they will be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rule The rule to add to the policy.
 * @returns A new [[Policy]] clone of the original one, with the new rule added.
 * @since Unreleased
 */
function addOptionalRuleUrl(policy, rule) {
    return addIri(policy, acp.anyOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Removes a rule that extends the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the required rules,
 * they will be granted access.
 * @param policy The [[Policy]] from which the rule should be removed.
 * @param rule The rule to remove from the policy.
 * @returns A new [[Policy]] clone of the original one, with the rule removed.
 * @since Unreleased
 */
function removeOptionalRuleUrl(policy, rule) {
    return removeIri(policy, acp.anyOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Overwrite the rule extending the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the required rules,
 * they will be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rules The rules the policy accepts.
 * @returns A new [[Policy]] clone of the original one, with the optional rules replaced.
 * @since Unreleased
 */
function setOptionalRuleUrl(policy, rule) {
    return setIri(policy, acp.anyOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the [[Rule]]'s accepted by the given [[Policy]]
 * @param policy The [[policy]] from which the rules should be read.
 * @returns A list of the optional [[Rule]]'s
 * @since unreleased
 */
function getOptionalRuleUrlAll(policy) {
    return getIriAll(policy, acp.anyOf);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Add a rule that restricts the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the forbidden rules,
 * they will **not** be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rule The rule to add to the policy.
 * @returns A new [[Policy]] clone of the original one, with the new rule added.
 * @since Unreleased
 */
function addForbiddenRuleUrl(policy, rule) {
    return addIri(policy, acp.noneOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Removes a rule that restricts the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the forbidden rules,
 * they will **not** be granted access.
 * @param policy The [[Policy]] from which the rule should be removed.
 * @param rule The rule to remove from the policy.
 * @returns A new [[Policy]] clone of the original one, with the rule removed.
 * @since Unreleased
 */
function removeForbiddenRuleUrl(policy, rule) {
    return removeIri(policy, acp.noneOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Set the rules restrincting the scope of a given the [[Policy]]. If an agent
 * requesting access to a resource is present in **any** of the required rules,
 * they will not be granted access.
 * @param policy The [[Policy]] to which the rule should be added.
 * @param rules The rules the policy accepts.
 * @returns A new [[Policy]] clone of the original one, with the optional rules replaced.
 * @since Unreleased
 */
function setForbiddenRuleUrl(policy, rule) {
    return setIri(policy, acp.noneOf, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the [[Rule]]'s forbidden by the given [[Policy]]
 * @param policy The [[policy]] from which the rules should be read.
 * @returns A list of the forbidden [[Rule]]'s
 * @since unreleased
 */
function getForbiddenRuleurlAll(policy) {
    return getIriAll(policy, acp.noneOf);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Initialise a new, empty [[Rule]].
 *
 * @param url URL that identifies this [[Rule]].
 */
function createRule(url) {
    const stringUrl = internal_toIriString(url);
    let ruleThing = createThing({ url: stringUrl });
    ruleThing = setUrl(ruleThing, rdf.type, acp.Rule);
    return ruleThing;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Get the [[Rule]] with the given URL from an [[RuleDataset]].
 *
 * @param ruleResource The Resource that contains the given [[Rule]].
 * @param url URL that identifies this [[Rule]].
 * @returns The requested [[Rule]], if it exists, or `null` if it does not.
 */
function getRule(ruleResource, url) {
    const foundThing = getThing(ruleResource, url);
    if (foundThing === null || getUrl(foundThing, rdf.type) !== acp.Rule) {
        return null;
    }
    return foundThing;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Gets the [[Rule]]s from a [[SolidDataset]].
 *
 * @param ruleResource The Resource that contains (zero of more) [[Rule]]s.
 * @returns The [[Rule]]s contained in this resource.
 */
function getRuleAll(ruleResource) {
    const things = getThingAll(ruleResource);
    return things.filter(isRule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Insert the given [[Rule]] into the given [[SolidDataset]], replacing previous
 * instances of that Rule.
 *
 * @param ruleResource The Resource that contains (zero of more) [[Rule]]s.
 * @returns A new RuleDataset equal to the given Rule Resource, but with the given Rule.
 */
function setRule(ruleResource, rule) {
    return setThing(ruleResource, rule);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * List all the agents a [[Rule]] applies **directly** to. This will not include agents
 * that are part of a group the [[Rule]] applies to, nor will it include specific agent
 * classes, such as authenticated or public agents.
 *
 * @param rule The rule from which agents are read.
 * @returns A list of the WebIDs of agents included in the rule.
 * @since Unreleased
 */
function getAgentAll(rule) {
    return getIriAll(rule, acp.agent).filter((agent) => agent !== acp.PublicAgent && agent !== acp.AuthenticatedAgent);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Overwrite the agents the [[Rule]] applies to with the provided agents.
 *
 * @param rule The rule for which agents are set.
 * @param agents The list of agents the rule should apply to.
 * @returns A copy of the input rule, applying to a different set of agents.
 * @since Unreleased
 */
function setAgent(rule, agent) {
    // Preserve the special agent classes authenticated and public, which we
    // don't want to overwrite with this function.
    const isPublic = hasPublic(rule);
    const isAuthenticated = hasAuthenticated(rule);
    let result = setIri(rule, acp.agent, agent);
    // Restore public and authenticated
    result = setPublic(result, isPublic);
    result = setAuthenticated(result, isAuthenticated);
    return result;
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Apply the [[Rule]] to an additional agent.
 *
 * @param rule The [[Rule]] to be applied to an additional agent.
 * @param agent The agent the [[Rule]] should apply to.
 * @returns A copy of the [[Rule]], applying to an additional agent.
 * @since Unreleased
 */
function addAgent(rule, agent) {
    return addIri(rule, acp.agent, agent);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Prevent the [[Rule]] from applying to a given agent directly. This will not
 * remove the agent from any groups the rule applies to.
 *
 * @param rule The [[Rule]] that should no longer apply to a given agent.
 * @param agent The agent the rule should no longer apply to.
 * @returns A copy of the rule, not applying to the given agent.
 * @since Unreleased
 */
function removeAgent(rule, agent) {
    return removeIri(rule, acp.agent, agent);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Lists all the groups a [[Rule]] applies to.
 *
 * @param rule The rule from which groups are read.
 * @returns A list of the [[URL]]'s of groups included in the rule.
 * @since Unreleased
 */
function getGroupAll(rule) {
    return getIriAll(rule, acp.group);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Overwrite the groups the [[Rule]] applies to with the provided groups.
 *
 * @param rule The rule for which groups are set.
 * @param agents The list of groups the rule should apply to.
 * @returns A copy of the input rule, applying to a different set of groups.
 * @since Unreleased
 */
function setGroup(rule, group) {
    return setIri(rule, acp.group, group);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Apply the [[Rule]] to an additional group.
 *
 * @param rule The [[Rule]] to be applied to an additional group.
 * @param agent The group the [[Rule]] should apply to.
 * @returns A copy of the [[Rule]], applying to an additional group.
 * @since Unreleased
 */
function addGroup(rule, group) {
    return addIri(rule, acp.group, group);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Prevent the [[Rule]] from applying to a given group.
 *
 * @param rule The [[Rule]] that should no longer apply to a given group.
 * @param agent The group the rule should no longer apply to.
 * @returns A copy of the rule, not applying to the given group.
 * @since Unreleased
 */
function removeGroup(rule, group) {
    return removeIri(rule, acp.group, group);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Check if the rule applies to any agent.
 *
 * @param rule The rule checked for public access.
 * @returns Whether the rule applies to any agent or not.
 */
function hasPublic(rule) {
    return (getIriAll(rule, acp.agent).filter((agent) => agent === acp.PublicAgent)
        .length > 0);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Enable or disable a rule from applying to any agent.
 *
 * @param rule The rule being modified.
 * @param hasPublic A boolean indicating whether the rule should apply or not to any agent.
 * @returns A copy of the rule, updated to apply/not apply to any agent.
 * @status Unreleased
 */
function setPublic(rule, hasPublic) {
    return hasPublic
        ? addIri(rule, acp.agent, acp.PublicAgent)
        : removeIri(rule, acp.agent, acp.PublicAgent);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Check if the rule applies to any authenticated agent.
 *
 * @param rule The rule checked for authenticated access.
 * @returns Whether the rule applies to any authenticated agent or not.
 */
function hasAuthenticated(rule) {
    return (getIriAll(rule, acp.agent).filter((agent) => agent === acp.AuthenticatedAgent).length > 0);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Enable or disable a rule from applying to any authenticated agent.
 *
 * @param rule The rule being modified.
 * @param hasPublic A boolean indicating whether the rule should apply or not to any authenticated agent.
 * @returns A copy of the rule, updated to apply/not apply to any authenticated agent.
 * @status Unreleased
 */
function setAuthenticated(rule, authenticated) {
    return authenticated
        ? addIri(rule, acp.agent, acp.AuthenticatedAgent)
        : removeIri(rule, acp.agent, acp.AuthenticatedAgent);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Check if the rule applies to the creator of the Resource.
 *
 * @param rule The rule checked for authenticated access.
 * @returns Whether the rule applies to the creator of the Resource or not.
 */
function hasCreator(rule) {
    return (getIriAll(rule, acp.agent).filter((agent) => agent === acp.CreatorAgent)
        .length > 0);
}
/**
 * ```{note} There is no Access Control Policies specification yet. As such, this
 * function is still experimental and subject to change, even in a non-major release.
 * ```
 *
 * Enable or disable a rule from applying to the creator of the Resource.
 *
 * @param rule The rule being modified.
 * @param hasPublic A boolean indicating whether the rule should apply or not to the creator of the Resource.
 * @returns A copy of the rule, updated to apply/not apply to the creator of the Resource.
 * @status Unreleased
 */
function setCreator(rule, creator) {
    return creator
        ? addIri(rule, acp.agent, acp.CreatorAgent)
        : removeIri(rule, acp.agent, acp.CreatorAgent);
}

var acpRule = /*#__PURE__*/Object.freeze({
    __proto__: null,
    addRequiredRuleUrl: addRequiredRuleUrl,
    removeRequiredRuleUrl: removeRequiredRuleUrl,
    setRequiredRuleUrl: setRequiredRuleUrl,
    getRequiredRuleUrlAll: getRequiredRuleUrlAll,
    addOptionalRuleUrl: addOptionalRuleUrl,
    removeOptionalRuleUrl: removeOptionalRuleUrl,
    setOptionalRuleUrl: setOptionalRuleUrl,
    getOptionalRuleUrlAll: getOptionalRuleUrlAll,
    addForbiddenRuleUrl: addForbiddenRuleUrl,
    removeForbiddenRuleUrl: removeForbiddenRuleUrl,
    setForbiddenRuleUrl: setForbiddenRuleUrl,
    getForbiddenRuleurlAll: getForbiddenRuleurlAll,
    createRule: createRule,
    getRule: getRule,
    getRuleAll: getRuleAll,
    setRule: setRule,
    getAgentAll: getAgentAll,
    setAgent: setAgent,
    addAgent: addAgent,
    removeAgent: removeAgent,
    getGroupAll: getGroupAll,
    setGroup: setGroup,
    addGroup: addGroup,
    removeGroup: removeGroup,
    hasPublic: hasPublic,
    setPublic: setPublic,
    hasAuthenticated: hasAuthenticated,
    setAuthenticated: setAuthenticated,
    hasCreator: hasCreator,
    setCreator: setCreator
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 *
 * ```{warning}
 * Do not use this function in production code.  For use in **unit tests** that require a
 * [[AccessControlResource]].
 * ```
 *
 * Initialises a new empty Access Control Resource for a given Resource for use
 * in **unit tests**.
 *
 * @param resourceUrl The URL of the Resource to which the mocked ACR should apply.
 * @returns The mocked empty Access Control Resource for the given Resource.
 */
function mockAcrFor(resourceUrl) {
    const acrUrl = new URL("access-control-resource", resourceUrl).href;
    const acr = Object.assign(mockSolidDatasetFrom(acrUrl), { accessTo: resourceUrl });
    return acr;
}
/**
 * ```{warning}
 * Do not use this function in production code.  For use in **unit tests** that require a
 * Resource with an [[AccessControlResource]].
 * ```
 *
 * Attaches an Access Control Resource to a given [[SolidDataset]] for use
 * in **unit tests**; e.g., unit tests that call [[getAccessControl]].
 *
 * @param resource The Resource to mock up with a new resource ACL.
 * @param accessControlResource The Access Control Resource to attach to the given Resource.
 * @returns The input Resource with an empty resource ACL attached.
 */
function addMockAcrTo(resource, accessControlResource = mockAcrFor(getSourceUrl(resource))) {
    const resourceWithAcr = Object.assign(internal_cloneResource(resource), {
        internal_acp: {
            acr: accessControlResource,
            aprs: {},
        },
    });
    return resourceWithAcr;
}

var acpMock = /*#__PURE__*/Object.freeze({
    __proto__: null,
    mockAcrFor: mockAcrFor,
    addMockAcrTo: addMockAcrTo
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/** @hidden */
const acp_v1 = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, acpAcp), acpControl), acpPolicy), acpRule), acpMock);

export { acp_v1, addBoolean, addDatetime, addDecimal, addInteger, addIri, addLiteral, addMockFallbackAclTo, addMockResourceAclTo, addNamedNode, addStringNoLocale, addStringWithLocale, addTerm, addUrl, asIri, asUrl, changeLogAsMarkdown, createAcl, createAclFromFallbackAcl, createContainerAt, createContainerInContainer, createSolidDataset, createThing, deleteAclFor, deleteContainer, deleteFile, deleteSolidDataset, getAgentAccess, getAgentAccessAll, getAgentDefaultAccess, getAgentDefaultAccessAll, getAgentResourceAccess, getAgentResourceAccessAll, getBoolean, getBooleanAll, getContentType, getDatetime, getDatetimeAll, getDecimal, getDecimalAll, getFallbackAcl, getFile, getFileWithAcl, getGroupAccess, getGroupAccessAll, getGroupDefaultAccess, getGroupDefaultAccessAll, getGroupResourceAccess, getGroupResourceAccessAll, getInteger, getIntegerAll, getIri, getIriAll, getLiteral, getLiteralAll, getNamedNode, getNamedNodeAll, getPodOwner, getPublicAccess, getPublicDefaultAccess, getPublicResourceAccess, getResourceAcl, getResourceInfo, getResourceInfoWithAcl, getSolidDataset, getSolidDatasetWithAcl, getSourceIri, getSourceUrl, getStringByLocaleAll, getStringNoLocale, getStringNoLocaleAll, getStringWithLocale, getStringWithLocaleAll, getTerm, getTermAll, getThing, getThingAll, getUrl, getUrlAll, hasAccessibleAcl, hasFallbackAcl, hasResourceAcl, hasResourceInfo, hasServerResourceInfo, isContainer, isPodOwner, isRawData, isThing, mockContainerFrom, mockFetchError, mockFileFrom, mockSolidDatasetFrom, mockThingFrom, overwriteFile, removeAll, removeBoolean, removeDatetime, removeDecimal, removeInteger, removeIri, removeLiteral, removeNamedNode, removeStringNoLocale, removeStringWithLocale, removeThing, removeUrl, saveAclFor, saveFileInContainer, saveSolidDatasetAt, saveSolidDatasetInContainer, setAgentDefaultAccess, setAgentResourceAccess, setBoolean, setDatetime, setDecimal, setInteger, setIri, setLiteral, setNamedNode, setPublicDefaultAccess, setPublicResourceAccess, setStringNoLocale, setStringWithLocale, setTerm, setThing, setUrl, solidDatasetAsMarkdown, thingAsMarkdown };
